/**
 * FlightTracker — the hill chart at the top of the Flight Plan (2026-08-27).
 *
 * A Basecamp Hill Chart (Ryan Singer / 37signals) rendered as an approach path, with planes
 * instead of dots. The left rail is the FLIGHT LINE: milestones waiting to depart. Drag one onto
 * the curve and it becomes a flight — which forces a crew assignment, because a plane does not
 * leave the ground without one.
 *
 * ── WHY A JUDGED POSITION, WHEN NEARLY EVERYTHING ELSE HERE IS DERIVED ────────────────────
 * Percent-complete is GENERATED: a side effect of ticking boxes that asserts nothing. This firm
 * has live proof it lies — four milestones sit at 100% checklist with no posted date. A hill
 * position is JUDGED: someone stopped, thought, and committed to a view they can be wrong about.
 * Matt: "That forcing of thinking through an actual status... It's active management rather than
 * assuming the percentage complete on a checklist represents the actual percent complete."
 *
 * The cost of the act is the mechanism, so it is deliberately manual, and the note is required
 * on every move. A hill chart without the note is a prettier percentage.
 *
 * UPHILL is figuring it out (unknowns, unestimable). The PEAK is "we know how to do this."
 * DOWNHILL is execution — and only there does a predicted landing date mean anything.
 *
 * ── THE CURVE ─────────────────────────────────────────────────────────────────────────────
 * A piecewise cubic smoothstep bell: flat, steepening, levelling at the peak, steepening down,
 * flattening again. Algebraic, exactly the shape asked for, and cheap to sample and differentiate.
 *
 * The plane's ATTITUDE IS THE TRUE TANGENT of that curve, mapped onto the 0/25/50 rotation scale
 * calibrated by eye with Matt (0 departing, 25 level, 50 on descent). Steeper reads as crashing,
 * so the mapping is capped there rather than using the raw ±52° tangent. This is the honest
 * version of what the plane used to do: it used to rotate off checklist percentage, which was the
 * exact signal the hill chart exists to replace.
 *
 * ── THE THREADS CURVE (2026-08-27) ────────────────────────────────────────────────────────
 * A second, smaller hill tracking Threads (this app's tracked entity — RFIs, Submittals,
 * coordination items — never a Slack discussion thread) with a helicopter icon. It shares the
 * same horizontal mapping (`PAD.l`/`innerW`) AND THE SAME GROUND LINE (`baseY`) as the
 * milestone curve — Matt: "the ground plane for threads should be shared with the milestones
 * ground plane... I want them to share the same page space" — at a smaller vertical amplitude
 * (`THREAD_H` vs `MAIN_H`), one hill nested inside the other's footprint rather than a separate
 * band underneath it. A thread at a given `t` sits directly under a flight at the same `t` —
 * one shared sense of "how far along." Its own SHAPE (`threadHill`, see its own doc comment)
 * is a deliberately steeper variant of `hill`, not `hill` itself.
 *
 * Unlike flights, a thread needs no "put in the air" ceremony to EXIST — it opens onto the
 * board the moment it's created (see api/thread-hill.js), no queued backlog to draw from. No
 * crew, no thread number — just the judged position and its required note, the same as flights
 * minus the parts that don't map onto a Thread.
 *
 * It still gets a visual queue, though (Round 10, 2026-08-27): a thread nobody has ever judged
 * sits at its exact seed position (`SEED_T`, 0.08) until someone moves it, so "still at 0.08" is
 * read as "never looked at" and shown in ThreadDeck's own stack at the gate — right next to
 * FlightDeck's — instead of cluttering the curve at t=0.08 before anyone's actually judged it.
 * Dragging it out is a normal thread move; nothing new in the data model, just a different
 * on-screen starting point.
 */

import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { PALETTE } from './MiniChart.jsx'
import { ActionMenu, ActionMenuItem, ActionMenuDivider, useActionMenu } from './ActionMenu.jsx'
import { getHillTimeline } from '../services/flights.js'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'
import CrewPairsTab from './CrewPairsTab.jsx'
import { wf } from '../outline/state.js'

// THE CAPTAINS MOVE THE PLANES (Matt, 2026-10-27): "do NOT allow the tower to move planes on the curve. It is
// now the job of the captains. The curve exists only to show where the captains have moved their planes."
// A plane already on the curve can be dragged only by its own captain (the daily captain's report moves it
// too). The tower still launches a plane from the gate stack, which is crewing, not moving.
const isMyPlane = (f) => !!(f && (f.crew || []).some(c => c.isCaptain && c.memberAirtableId && c.memberAirtableId === wf.currentMemberId))

/* ── curve math ──────────────────────────────────────────────────────────────────────────── */

/** Height 0..1 across t 0..1. Flat → steep → level → steep → flat. */
// One shared empty list: an absent collection must never read as "changed" to a memo or effect.
const EMPTY = []

export function hill(t) {
  const u = t <= 0.5 ? t / 0.5 : (1 - t) / 0.5
  return 3 * u * u - 2 * u * u * u
}
/** dy/dt. Positive climbing, zero at both ends and at the peak, negative descending. */
export function hillSlope(t) {
  const u = t <= 0.5 ? 2 * t : 2 * (1 - t)
  const d = 12 * u * (1 - u)
  return t <= 0.5 ? d : -d
}
/**
 * Slope → the plane's rotation, on Matt's calibrated scale: 0 climbing, 25 level, 50 descending.
 * Max |slope| is 3, so this spans exactly 0..50 and never reaches the angles that read as a crash.
 */
export function attitudeFor(t) {
  return 25 - (Math.max(-3, Math.min(3, hillSlope(t))) / 3) * 25
}

// The threads curve's own, slightly steeper shape. Round 2 (2026-08-28) — Matt: "The curve for
// the helicopters... tends to track along the ground toward the beginning and end while the
// airplane curve does not. I want the apex... in the same location but I want the curve slope
// increased at the beginning and end so it doesn't run along the ground."
//
// The PREVIOUS approach (windowing `hill`'s own symmetric fold into a narrower band with flat
// shoulders clamped outside it) is exactly what produced that ground-hugging: the shoulders
// were LITERALLY flat — zero height — for a real stretch near both ends, not just the single
// instant of zero slope a vanilla smoothstep already has at t=0/t=1. Replaced with a power
// remap of the same fold instead: `v = u^THREAD_EASE_EXP` before running it through the
// identical `3v²-2v³` smoothstep polynomial. At exponent 1 this reproduces `hill` exactly (u^1
// = u); an exponent BELOW 1 makes v rise faster than u for every u in (0,1) — the curve leaves
// the ground climbing, continuously, from the very first instant past t=0 — while u=1 (and so
// v=1) only ever happens at the true peak (t=0.5), so the apex sits at exactly the same
// location and height as before. No flat region anywhere except the single mathematical point
// at t=0/t=1 itself, same as any smoothstep.
//
// Round 3, same day — Matt: "I don't want the helicopter curve to start run higher then lower
// than the plane curve. Have them appear to start around the same sloping angle, but still
// keep the apex of the helicopter where it is." 0.7 pushed the curve up so fast near the edges
// that — even scaled by the smaller THREAD_H — it briefly rose ABOVE the milestone curve's own
// (taller-amplitude, but much slower to leave the ground) line before dropping back beneath
// it. This is a real mathematical tension, not just a tuning miss: `hill` itself falls off like
// t³ near the edges while any exponent<1 falls off slower — for ANY exponent short of exactly
// 1, threadHill/hill diverges arbitrarily close to t=0, so some crossing is unavoidable in the
// strict limit. What matters is keeping it too small to see: at 0.93, the crossover is confined
// to t<0.0085 — under one 120th of the plotted range, i.e. sub-sample, invisible in the
// rendered path — while still landing meaningfully above vanilla `hill` (checked numerically)
// everywhere past that point, which is what actually fixes the ground-hugging complaint.
const THREAD_EASE_EXP = 0.93   // < 1 = steeper departure/arrival; 1 reproduces `hill` exactly
export function threadHill(t) {
  const u = t <= 0.5 ? t / 0.5 : (1 - t) / 0.5   // same fold hill() itself uses
  if (u <= 0) return 0
  const v = Math.pow(u, THREAD_EASE_EXP)
  return 3 * v * v - 2 * v * v * v
}
export function threadHillSlope(t) {
  const u = t <= 0.5 ? t / 0.5 : (1 - t) / 0.5
  if (u <= 0) return 0   // avoids a 0×Infinity NaN right at the exact edge — the true limit is 0 anyway
  const v = Math.pow(u, THREAD_EASE_EXP)
  const dHillDv = 6 * v - 6 * v * v
  const dvDu = THREAD_EASE_EXP * Math.pow(u, THREAD_EASE_EXP - 1)
  const d = dHillDv * dvDu * 2   // du/dt is ±2 depending on side — applied via the sign below
  return t <= 0.5 ? d : -d
}
/**
 * Same baseline attitude as `attitudeFor` (25deg = level, matching the plane's convention) but a
 * much smaller swing — the threads curve's own slope is intentionally steeper than the flights
 * curve's (see Round 2's comment above), so reusing the plane's full ±25deg swing on top of that
 * steeper slope saturated the clamp for a long stretch of the descending right side, reading as
 * a helicopter about to crash rather than a gentle nose-down glide (Matt, 2026-08-31). Halved to
 * ±12deg — still readable as climbing/descending, nowhere near as alarming.
 */
export function threadAttitudeFor(t) {
  return 25 - (Math.max(-3, Math.min(3, threadHillSlope(t))) / 3) * 12
}

const STALL_DAYS = 7   // a plane nobody has moved in a week is the alarm, not the position
const LANDING_T = 0.96  // "moved all the way to the right" — close enough to try posting it
// "Grow and glow" (2026-09-03) — the on-curve highlight when the matching row is hovered in the
// "In the air" list below the chart. Doubled drop-shadow (a plain single drop-shadow reads as
// flat/weak at this icon size) in the same blue every "planned/neutral" flight already uses.
const GLOW_FILTER = `drop-shadow(0 0 3px ${PALETTE.blue}) drop-shadow(0 0 7px ${PALETTE.blue})`
// Chart padding — module scope since 2026-09-01 (originally hoisted for a curve-relative hover
// positioning scheme since replaced by FloatingCard's own real-DOM-coordinate approach — see its
// doc comment). Left here regardless: a plain literal that never varies per render, and pt/pt2
// below still read it.
const PAD = { l: 104, r: 104, t: 34, b: 46 }

// A brand-new thread seeds at exactly t=0.08 (api/thread-hill.js) and stays there until a human
// deliberately judges it elsewhere — so "still at 0.08" is a precise, no-backend-changes-needed
// signal for "nobody's touched this yet," used to decide whether a thread sits in the waiting
// stack at the gate or out on the curve. See queuedThreads' own comment below.
export const SEED_T = 0.08
export const isSeedThread = (th) => Math.abs((th && th.hillPosition != null ? th.hillPosition : SEED_T) - SEED_T) < 0.001
// Where a menu-driven "Put in the air" lands something: just past the seed, still reading
// "Just departed" — far enough from SEED_T that a thread placed this way stops counting as
// queued (isSeedThread goes false) and renders on the curve.
export const TAKEOFF_T = 0.1

const HOVER_W = 268   // must match S.hover's own width below

// A brand-new thread seeds at exactly t=0.08 and nothing moves it until someone judges it, so
// with dozens (in practice, hundreds) of threads open at once a lot of them land on the EXACT
// same point — they don't just look crowded, they render one directly on top of another with
// nothing to tell them apart or click. Matt: "make the helicopters stack like the planes do" —
// the same offset+shrink+fade cascade the flight deck's own closed peek already uses for a pile
// of waiting planes, applied here to whatever piles up ON the curve itself. Bucketing by t (not
// a live pairwise distance check) is deliberately the simple option: everything sharing a bucket
// got there by sitting at the same or near-same position, which is exactly what needs
// separating, with no pairwise-distance chaining to get wrong with nothing to render it back
// against and check.
//
// ROUND 2 OF THIS FIX: the first version capped how far the offset could GROW past
// VISIBLE_STACK_CAP items but kept assigning one to every item in the bucket regardless — with
// a cluster of 197 threads (Matt's real count, not the two dozen this was built against), items
// 6 through 197 all landed on the exact same clamped offset, i.e. exactly the original bug,
// just moved one step over. Fixed properly this time: only the first VISIBLE_STACK_CAP items in
// a bucket get their own stacked slot at all. Everything past that is marked `skip` (the caller
// renders nothing for them individually — no silent truncation, though: `overflowFor()` below
// reports the hidden count per bucket so a "+192" badge can say what's not shown, the same
// "never a silent cap" rule the flight line's own deck already follows for its own overflow.
const CLUSTER_T = 0.02
const VISIBLE_STACK_CAP = 5
function declutterOffsets(items) {
  const buckets = new Map()
  for (const it of items) {
    const key = Math.round(it.t / CLUSTER_T)
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push(it)
  }
  const offsets = new Map()     // item key -> { dx, dy, scale, opacity, z } | { skip: true }
  const overflow = []           // [{ t, count }] — one per bucket with more than the cap
  for (const arr of buckets.values()) {
    if (arr.length < 2) continue   // a lone plane needs no offset
    arr.forEach((it, k) => {
      if (k >= VISIBLE_STACK_CAP) { offsets.set(it.key, { skip: true }); return }
      offsets.set(it.key, {
        dx: k * 6, dy: k * 6,
        scale: Math.max(0.55, 1 - k * 0.1),
        opacity: Math.max(0.35, 1 - k * 0.12),
        z: 10 - k,
      })
    })
    if (arr.length > VISIBLE_STACK_CAP) {
      overflow.push({ t: arr[0].t, count: arr.length - VISIBLE_STACK_CAP })
    }
  }
  return { offsets, overflow }
}

const dayKey = (iso) => String(iso).slice(0, 10)
const fmtLongDay = (k) => {
  const d = new Date(k + 'T00:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })
}

/**
 * Replay the hill on a past date from the append-only move log.
 *
 * A flight's position on day K is its last move at or before the END of that day. A flight with
 * no move by then did not exist yet and is omitted — it should not appear in its own past.
 * Flights that have since landed DO appear, faded, because what was in the air three weeks ago
 * is the actual question being asked.
 */
function replayAt(timeline, key) {
  if (!timeline) return null
  const cutoff = key + 'T23:59:59.999Z'
  const last = new Map()
  for (const h of timeline.history) {
    if (h.movedAt <= cutoff) last.set(h.flightId, h)
  }
  const out = []
  for (const f of timeline.flights) {
    const h = last.get(f.flightId)
    if (!h) continue
    if (f.landedAt && dayKey(f.landedAt) < key) continue   // already landed by then
    out.push({
      ...f,
      hillPosition: h.position,
      hillMovedAt: h.movedAt,
      stillDays: Math.floor((new Date(key + 'T12:00:00Z') - new Date(h.movedAt)) / 86400000),
      crew: [],
      movedToday: dayKey(h.movedAt) === key,
      note: dayKey(h.movedAt) === key ? h.note : null,
    })
  }
  return out
}

const reduceMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/* ── component ───────────────────────────────────────────────────────────────────────────── */

export default function FlightTracker({
  flights, projects, today, onDropNew, onMoveHill, onOpenFlight, busy, checklists,
  // Stable EMPTY defaults (2026-09-24): a `|| []` here or in a memo below is a new array every render,
  // and these lists feed the queue-report effect — see its own comment for the loop that caused.
  threads = EMPTY, onMoveThreadHill, onOpenThread, showThreads = true,
  landed, onOpenLanded, landedThreads = EMPTY, onOpenLandedThread,
  crewFlying, crewAvailable, crewTable,
  // Pilots tab (2026-09-10): flights with an open seat, for the "+ Add to flight" picker on a
  // grounded slot, plus the two mutations the tab needs to edit a crew seat in place — swap a
  // seat's own 1st/2nd rank, or seat someone eligible onto one of these flights directly from
  // the list instead of hunting for the flight on the curve first.
  assignableFlights, onChangeCrewPriority, onAssignCrewToFlight,
  onQueueChange,
  // A click (not a drag) on an on-curve flight/thread opens the SAME action menu its row in
  // "In the air" already has — see OnCurveFlight/OnCurveThread below.
  onCrew, onLand, onPull, onFastTrackThread, onRemoveSquadronThread, onAddSquadronThread, onCrewThread,
  onMoveSquadronHill,
  // Round 18: FlightPlanTab's own board-reload — lives in this header now, not a since-removed
  // stats card. It genuinely isn't redundant with the workspace-level Refresh button above this
  // whole tab (that one reloads the Anatomy tab's data, never the flight plan's).
  onRefresh, refreshing,
  // True while any of FlightPlanTab's modals is open. Hover cards are force-cleared and
  // suppressed for the duration — a leftover card can't be released by pointer events once an
  // overlay (especially the peek modal's iframe) sits on top, so we stop depending on them.
  overlayOpen,
  // A flightId or threadId hovered in FlightPlanTab's own "In the air" list below this chart
  // (2026-09-03, Matt: "cause that icon in the curve to slightly grow and glow so I can see
  // where it is on the line at a glance"). Matched against both flightId and threadId below —
  // the two id spaces never collide (Supabase flights.id vs. Airtable recXXX), so one prop
  // covers flights, squadrons, AND threads without needing three separate ids from the caller.
  hoverRowId,
}) {
  const chartRef = useRef(null)
  const [size, setSize] = useState({ w: 900, h: 300 })
  const [drag, setDrag] = useState(null)      // { kind:'flight'|'new'|'thread', id, t, x, y }
  // A flight or thread frozen at its dropped position while the required move-note prompt
  // (async, in FlightPlanTab) is answered and saved. Without this the plane visibly snaps back
  // to its old spot the instant the pointer lifts — `drag` clears immediately on pointerup, so
  // between then and the save landing there was nothing holding it at the new point.
  const [pending, setPending] = useState(null) // { kind:'flight'|'thread', id, t }
  const [hover, setHover] = useState(null)
  // CLUSTERS (Matt, 2026-10-27: planes close together were hard to hover): { ids, x, y } while the pointer is
  // on a bunch of planes within CLUSTER_PX of each other; one card then lists them all (ClusterCard).
  // PINNED (2026-10-27, "popupgeddon"): one popup at a time. Hover peeks (info only); a click pins the same card
  // and gives it the actions. { ids, sel } — ids = the plane's bunch, sel = the plane shown in detail (null = the list).
  const [pinned, setPinned] = useState(null)
  const [threadHover, setThreadHover] = useState(null)
  const [scope, setScope] = useState('all')   // 'all' | projectId
  // Which view occupies the chart's own footprint — the flight curve, or the pilots status
  // board. Matt (2026-09-10): the pilots table used to live behind a hover on the "N in the
  // air / N available" stat, which he called "too hidden" — now a real tab, sharing the same
  // space the curve draws in, so switching between them is one click instead of a hover.
  const [boardView, setBoardView] = useState('curve')  // 'curve' | 'crews' | 'pilots'
  const canSeeCrews = isSeatLoaded() && (hasModule('workload') || hasModule('admin'))
  const [drawn, setDrawn] = useState(false)
  const [timeline, setTimeline] = useState(null)
  const [dayIdx, setDayIdx] = useState(null)  // null = live; otherwise an index into `days`
  // The landing FLIGHT-OVER: a fast-tracked item flies the rest of its curve to the landing
  // zone instead of just vanishing (Matt: "I expected the helicopter to animate and fly the
  // curve, land on the other side"). Driven by the 'flight-tracker-land-anim' CustomEvent
  // (dispatched by FlightPlanTab's doFastTrackThread the moment a fast-track is committed —
  // whichever entry point started it: deck menu, curve menu, queue row, or drag-to-end),
  // animated by rAF along the REAL curve (pt2/pt at each frame, not a straight CSS glide),
  // while the item itself is hidden from every normal render so there's only ever one of it
  // on screen. Cleared after a short hold at the landing zone; the reload's fresh landed
  // stack takes over from there.
  const [landAnim, setLandAnim] = useState(null)  // { kind:'thread'|'flight', id, t }
  const landAnimRaf = useRef(0)

  // Force-clear the on-curve hover cards whenever an overlay opens — see overlayOpen's comment
  // in the props list. (The deck stacks clear their own internal hover state the same way.)
  useEffect(() => {
    if (overlayOpen) { setHover(null); setThreadHover(null) }
  }, [overlayOpen])

  // Loaded once, lazily — the board is fully usable before the history arrives.
  useEffect(() => { getHillTimeline(120).then(setTimeline).catch(() => {}) }, [])

  // Step through the days something ACTUALLY MOVED, not every calendar day. Five identical
  // frames for a quiet week teaches nothing; the point is to watch movement.
  const days = useMemo(() => {
    if (!timeline) return []
    return [...new Set(timeline.history.map(h => dayKey(h.movedAt)))].sort()
  }, [timeline])

  const past = dayIdx != null && days[dayIdx] ? replayAt(timeline, days[dayIdx]) : null
  const shown = past || flights
  const readOnly = past != null

  // Squadrons (database/202_squadrons.sql) and thread-crew flights (database/
  // 204_crew_two_and_thread_crew.sql, kind: 'squadron'/'thread') are `flights` rows too, but
  // NEITHER belongs on this curve. Squadrons render on the THREADS curve (pt2) — they're bundles
  // of threads, not milestones. A thread-crew flight is never rendered as its own entity
  // ANYWHERE — its whole purpose is a capacity-accounting record the client joins back onto the
  // real thread by id (see FlightPlanTab.jsx's threadCrewByThreadId). A real 2026-09-01 bug:
  // this filter only excluded 'squadron', so crewing a thread made a second, ghost "plane"
  // (milestoneId null, name = the thread's own title) appear via OnCurveFlight on the MAIN
  // flights curve — Matt: "an errant plane that appears to be a duplicate of a thread
  // masquerading as a milestone... splintered off from its helicopter." Explicit allowlist
  // instead of a single exclusion, so a THIRD non-milestone kind can never repeat this.
  const shownFlights = useMemo(
    () => shown.filter(f => (f.kind === 'milestone' || !f.kind) && (scope === 'all' || f.projectId === scope)),
    [shown, scope])
  // Same project scoping as scopedThreads just below — a squadron belongs to one project, so it
  // should filter the same way (2026-09-01, alongside adding it drag/declutter support at all).
  const shownSquadrons = useMemo(
    () => shown.filter(f => f.kind === 'squadron' && (scope === 'all' || f.projectId === scope)),
    [shown, scope])

  // Declutter — see declutterOffsets' own doc comment. Recomputed off each item's RESTING t (its
  // own hillPosition, or the settling t while a move is being saved) so the offsets settle back
  // to a fresh grouping once a drag finishes; the item actively being dragged is excluded below,
  // at render time, since it must track the cursor exactly rather than sit in a stack slot.
  const flightDeclutter = useMemo(() => declutterOffsets(shownFlights.map(f => ({
    key: f.flightId,
    t: (pending && pending.kind === 'flight' && pending.id === f.flightId) ? pending.t : f.hillPosition,
  }))), [shownFlights, pending])
  // Squadrons get their own declutter pass now too (2026-09-01) — Matt reported two squadrons
  // landing in the exact same spot with no way to tell them apart. A dedicated pass rather than
  // folding squadrons into threadDeclutter below: they're visually a different (bigger, fan-out)
  // icon, and squadron-vs-squadron overlap is the reported problem, not squadron-vs-thread.
  const squadronDeclutter = useMemo(() => declutterOffsets(shownSquadrons.map(f => ({
    key: f.flightId,
    t: (pending && pending.kind === 'squadron' && pending.id === f.flightId) ? pending.t : f.hillPosition,
  }))), [shownSquadrons, pending])
  // Threads narrow to the same project scope milestones already do — Matt: "Threads in the
  // helicopter list and on the curve should filter per the project filter just like milestones
  // do." Every consumer below (the deck, the declutter/overflow math, the on-curve render, the
  // hover lookup) reads this instead of the raw `threads` prop.
  const scopedThreads = useMemo(
    () => scope === 'all' ? (threads || EMPTY) : (threads || EMPTY).filter(th => th.projectId === scope),
    [threads, scope])

  // The finished stack narrows to the same project scope everything else on the board does
  // (Matt: filtering by project should filter the curve, the finished stack, AND next up — not
  // just next up). `landed`/`landedThreads` are the recently-posted-milestone / recently-closed-
  // thread lists behind LandedStacks; unlike the curve and Next Up they had no scope filter at
  // all until now.
  const scopedLanded = useMemo(
    () => scope === 'all' ? (landed || []) : (landed || []).filter(f => f.projectId === scope),
    [landed, scope])
  const scopedLandedThreads = useMemo(
    () => scope === 'all' ? (landedThreads || EMPTY) : (landedThreads || EMPTY).filter(th => th.projectId === scope),
    [landedThreads, scope])

  // Excludes anything still queued at the gate (isSeedThread) — those never render on-curve at
  // all (see the threads map below), so they must not feed the on-curve overflow badge either.
  // Leaving them in here was a bug: with ~190 threads all still sitting at the exact same seed
  // t, this alone produced a bogus "+189" badge floating on the curve right where nothing was
  // actually drawn — the on-curve items were correctly hidden, but the CLUSTER COUNT computed
  // from the full unfiltered list didn't know that.
  const threadDeclutter = useMemo(() => declutterOffsets(scopedThreads
    .filter(th => !isSeedThread(th))
    .map(th => ({
      key: th.threadId,
      t: (pending && pending.kind === 'thread' && pending.id === th.threadId) ? pending.t : th.hillPosition,
    }))), [scopedThreads, pending])

  // Threads still sitting at their exact seed position (0.08 — see api/thread-hill.js) have never
  // been given a judged position at all; nobody has looked at them yet. Matt: "Stack the
  // helicopters also on the left, right next to them [the planes]. Then let us pull them into
  // the flight path from there" — so an untouched thread now lives in a stack at the gate,
  // exactly like a waiting flight, instead of cluttering the curve at t=0.08 before anyone's
  // judged it. The moment it's dragged anywhere else, `onMoveThreadHill` gives it a real judged
  // position and it graduates onto the curve like any other thread — no new state to track,
  // just this one comparison. Excludes whatever's actively being dragged off the deck (that's
  // rendered as the on-curve ghost instead — see the threads map below).
  const queuedThreads = useMemo(
    () => scopedThreads.filter(th => isSeedThread(th)
      && !(drag && drag.kind === 'thread' && drag.id === th.threadId)
      && !(landAnim && landAnim.kind === 'thread' && landAnim.id === th.threadId)),
    [scopedThreads, drag, landAnim])

  // The landing flight-over listener — see landAnim's own comment above. Three phases:
  //   'fly'     — ease-in-out along the remaining curve (t interpolated, drawn via pt/pt2)
  //   'dock'    — banks off the curve's end into the landed stack's bottom slot, shrinking to
  //               stack size and leveling out (Matt: "It should look like the stack is moving
  //               up to allow the newly finished plane or helicopter to slide in underneath" —
  //               the stack's own make-room shift is driven off this same state, see
  //               LandedStacks' `landing` prop)
  //   'settled' — sits IN the bottom slot until the reload's fresh landed list actually
  //               contains it, then clears — and because the stack held everyone one slot up
  //               while it waited, the real item renders exactly where the ghost sat and the
  //               older items don't move a pixel on the swap. No timing coupling to the reload.
  // Respects prefers-reduced-motion (no ghost, the item just lands on the next reload).
  useEffect(() => {
    const onAnim = (e) => {
      const d = e.detail || {}
      if (!d.id || !d.kind || reduceMotion()) return
      let startT = d.t
      if (startT == null) {
        if (d.kind === 'thread') {
          const th = (threads || []).find(x => x.threadId === d.id)
          startT = th ? th.hillPosition : SEED_T
        } else {
          const f = (flights || []).find(x => x.flightId === d.id || x.milestoneId === d.id)
          startT = f ? f.hillPosition : SEED_T
        }
      }
      const begin = performance.now()
      // Fixed pace per unit of curve (a gate-to-landing flight takes ~1.6s, a drag-drop at the
      // threshold just hops the last stretch) with a floor so even the hop reads as motion.
      const FLY = Math.max(500, 1600 * (1 - startT))
      const DOCK = 480
      cancelAnimationFrame(landAnimRaf.current)
      const step = (now) => {
        const el = now - begin
        if (el <= FLY) {
          const p = el / FLY
          const ease = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          setLandAnim({ kind: d.kind, id: d.id, phase: 'fly', t: startT + (1 - startT) * ease })
          landAnimRaf.current = requestAnimationFrame(step)
        } else if (el <= FLY + DOCK) {
          const p = (el - FLY) / DOCK
          setLandAnim({ kind: d.kind, id: d.id, phase: 'dock', p: 1 - Math.pow(1 - p, 3) })   // ease-out into the slot
          landAnimRaf.current = requestAnimationFrame(step)
        } else {
          setLandAnim({ kind: d.kind, id: d.id, phase: 'settled' })
        }
      }
      landAnimRaf.current = requestAnimationFrame(step)
    }
    window.addEventListener('flight-tracker-land-anim', onAnim)
    return () => { window.removeEventListener('flight-tracker-land-anim', onAnim); cancelAnimationFrame(landAnimRaf.current) }
  }, [threads, flights])

  // Settled → swap: clear the ghost the moment the reload's landed list actually contains the
  // item (rendered pixel-identically in the slot the ghost was holding). 12s safety timeout so
  // a failed fast-track can never park a ghost on the stack forever.
  useEffect(() => {
    if (!landAnim || landAnim.phase !== 'settled') return
    const present = landAnim.kind === 'thread'
      ? (landedThreads || []).some(x => x.threadId === landAnim.id)
      : (landed || []).some(x => x.flightId === landAnim.id || x.milestoneId === landAnim.id)
    if (present) { setLandAnim(null); return }
    const to = setTimeout(() => setLandAnim(null), 12000)
    return () => clearTimeout(to)
  }, [landAnim, landedThreads, landed])

  // Keyed on boardView (2026-09-10): the chart div unmounts while the Pilots tab is up. With an
  // empty dep list the observer fired one last 0x0 reading as the div left the DOM, innerW fell
  // to its 120px floor, and nothing re-observed the new div when the curve came back — so the
  // hill came back squished to the left. Re-run per view, and ignore a zero-width reading.
  useEffect(() => {
    const el = chartRef.current
    if (!el) return
    const measure = () => { if (el.clientWidth > 0) setSize({ w: el.clientWidth, h: el.clientHeight }) }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [boardView])

  // Draw the curve in once on mount. Purely an entrance; nothing depends on it.
  useEffect(() => {
    if (reduceMotion()) { setDrawn(true); return }
    const id = requestAnimationFrame(() => setDrawn(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // The flight deck and thread deck (static Basecamp-dots stacks of waiting milestones/threads —
  // see FlightDeck/ThreadDeck below) live in the left margin, anchored at the curve's own t=0
  // ground point. The landed deck (recently-posted flights) mirrors them in the right margin.
  // Both margins stay equal (104) from Round 8, when they briefly needed to hold a wide hover
  // fan; the fans are gone (Round 10 — plain static stacks take far less room), but there's no
  // reason to re-litigate the symmetry now that it costs nothing.
  //
  // MAIN_H is a fixed constant, not derived from the measured chart height, so it can never be
  // stretched by anything sharing the container — the exact bug this file has shipped once
  // already (a flex minHeight that stretched the hill "to a needle"). Only width stays
  // responsive (`size.w`, via ResizeObserver).
  //
  // THE THREADS CURVE SHARES THIS SAME GROUND LINE (baseY) — Matt: "the ground plane for
  // threads should be shared with the milestones ground planes. Even though they are two
  // different curves, I want them to share the same page space." So `pt2` is the identical
  // formula as `pt`, just with a smaller amplitude (`THREAD_H` in place of `MAIN_H`) — one hill
  // nested inside the footprint of the other, not a separate band underneath it.
  const MAIN_H = 300
  // Bumped 80→170 (2026-08-28) — Matt: "Bring the apex of the helicopter curve up." Still
  // smaller than MAIN_H (300), just no longer so small the peak read as barely-there next to
  // the milestone curve's own.
  const THREAD_H = 170      // the threads curve's own, smaller amplitude — same baseY as above
  const CHART_H = PAD.t + MAIN_H + PAD.b
  const innerW = Math.max(120, size.w - PAD.l - PAD.r)
  const innerH = MAIN_H
  const baseY = PAD.t + innerH
  const pt = useCallback((t) => ({
    x: PAD.l + t * innerW,
    y: baseY - hill(t) * innerH,
  }), [innerW, innerH, baseY, PAD.l])
  // NEAREST-PLANE ZONES (Matt, 2026-10-27): the space around each plane belongs to it, so whichever plane is
  // nearest the pointer (within NEAR_PX) is the one hovered; no hunting for a 26px icon. Planes within
  // CLUSTER_PX of that one are a cluster and share one card. Positions match the render below exactly
  // (the resting t plus the declutter offset). Hover cards never take the pointer, so they never block
  // the planes behind them; a plane's actions are on its click menu.
  const flightPts = useMemo(() => {
    const m = new Map()
    for (const f of shownFlights) {
      const t = (pending && pending.kind === 'flight' && pending.id === f.flightId) ? pending.t : f.hillPosition
      const off = flightDeclutter.offsets.get(f.flightId)
      if (off && off.skip) continue
      const p0 = pt(t)
      m.set(f.flightId, off ? { x: p0.x + off.dx, y: p0.y + off.dy } : p0)
    }
    return m
  }, [shownFlights, flightDeclutter, pending, pt])
  // Bunches are worked out ONCE from where the planes sit (planes chained within CLUSTER_PX of each other), so
  // the count never changes as the pointer moves across them (it used to be measured from the nearest plane).
  const groupOf = useMemo(() => {
    const ids = [...flightPts.keys()], parent = new Map(ids.map(id => [id, id]))
    const find = (x) => { while (parent.get(x) !== x) x = parent.get(x); return x }
    for (let i = 0; i < ids.length; i++) for (let j = i + 1; j < ids.length; j++) {
      const a = flightPts.get(ids[i]), b = flightPts.get(ids[j])
      if (Math.hypot(a.x - b.x, a.y - b.y) <= CLUSTER_PX) parent.set(find(ids[i]), find(ids[j]))
    }
    const groups = new Map()
    for (const id of ids) { const r = find(id); if (!groups.has(r)) groups.set(r, []); groups.get(r).push(id) }
    const out = new Map()
    for (const g of groups.values()) for (const id of g) out.set(id, g)
    return out
  }, [flightPts])
  const onChartPointerMove = useCallback((e) => {
    if (drag || !chartRef.current) return
    if (e.target && e.target.closest && e.target.closest('.ft-hover-card')) return
    const r = chartRef.current.getBoundingClientRect()
    const mx = e.clientX - r.left, my = e.clientY - r.top
    let best = null, bestD = NEAR_PX
    for (const [id, q] of flightPts) { const d = Math.hypot(q.x - mx, q.y - my); if (d < bestD) { best = id; bestD = d } }
    setHover(h => (h === best ? h : best))
  }, [drag, flightPts])
  const onChartPointerLeave = useCallback(() => { setHover(null) }, [])
  const hoverGroup = hover ? (groupOf.get(hover) || [hover]) : null
  const cluster = !pinned && hoverGroup && hoverGroup.length > 1 && flightPts.get(hover)
    ? { ids: hoverGroup, x: Math.min(...hoverGroup.map(id => flightPts.get(id).x)), y: flightPts.get(hoverGroup[0]).y }
    : null
  const pinPlane = useCallback((f) => {
    const ids = groupOf.get(f.flightId) || [f.flightId]
    setHover(null)
    setPinned({ ids, sel: ids.length > 1 ? null : f.flightId })
  }, [groupOf])
  // A pinned card closes on Escape or a press anywhere outside it (a press on a plane pins that plane instead).
  useEffect(() => {
    if (!pinned) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setPinned(null) }
    const onDown = (e) => {
      const el = e.target
      if (el && el.closest && (el.closest('.ft-hover-card') || el.closest('[data-ft-icon]'))) return
      setPinned(null)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('pointerdown', onDown, true)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('pointerdown', onDown, true) }
  }, [pinned])
  useEffect(() => { if (overlayOpen) setPinned(null) }, [overlayOpen])
  const actionsFor = (f) => ({
    open:   () => { setPinned(null); onOpenFlight && onOpenFlight(f) },
    crew:   () => { setPinned(null); onCrew && onCrew(f) },
    land:   () => { setPinned(null); onLand && onLand(f, 'posted') },
    pull:   () => { setPinned(null); onLand && onLand(f, 'released') },
    cancel: () => { setPinned(null); onLand && onLand(f, 'cancelled') },
  })

  const pt2 = useCallback((t) => ({
    x: PAD.l + t * innerW,
    y: baseY - threadHill(t) * THREAD_H,
  }), [innerW, baseY, PAD.l])

  const curvePath = useMemo(() => {
    let d = ''
    for (let i = 0; i <= 120; i++) {
      const p = pt(i / 120)
      d += (i ? ' L ' : 'M ') + p.x.toFixed(1) + ',' + p.y.toFixed(1)
    }
    return d
  }, [pt])
  const curvePath2 = useMemo(() => {
    let d = ''
    for (let i = 0; i <= 120; i++) {
      const p = pt2(i / 120)
      d += (i ? ' L ' : 'M ') + p.x.toFixed(1) + ',' + p.y.toFixed(1)
    }
    return d
  }, [pt2])

  /* ── the flight line ──────────────────────────────────────────────────────────────────── */

  // Crowding is the real problem: ~215 open milestones. In firm scope the line shows each
  // project's NEXT departure only — one plane per project, which is the same filter that made
  // the queue readable. Narrowing to a project opens that project's whole line.
  //
  // NO DUE-DATE WINDOW (removed 2026-09-23, Matt). The line used to drop anything due more than
  // 21 days out; that hid Verheyen's Permit Plan Check Drawings from Brandon when it was ready to
  // start (predecessor posted, due Oct 22). A milestone ready to fly is shown whatever its date;
  // priority order already puts the urgent ones on top.
  const rawLineItems = useMemo(() => {
    const out = []
    for (const p of (projects || [])) {
      if (scope === 'all') {
        // A blocked next milestone (its Predecessor hasn't posted) has nothing to do here —
        // there is no point putting a plane on the line that cannot actually depart. It still
        // shows on the project's own row (and in the picker) so it isn't hidden entirely, just
        // not offered as something to drag onto the hill right now.
        if (p.next && !p.next.blocked) out.push({ ...p.next, projectName: p.projectName, openCount: p.openCount })
      } else if (p.projectId === scope) {
        for (const m of (p.milestones || [])) { if (!m.blocked) out.push({ ...m, projectName: p.projectName }) }
      }
    }
    return out
  }, [projects, scope])

  const lineItems = useMemo(() => {
    const soon = rawLineItems.slice()
    // PRIORITY ORDER (Phase 4 — docs/PRIORITY_ENGINE.md §13). Highest CD3 score first, so the
    // plane on top of the stack is the one worth starting, not merely the one due soonest.
    // Phase 0 measured the old due-date sort at r = 0.998 against the Priority Score it was
    // meant to differ from — sorting by `latestFinish` here was that same sort by another route.
    //
    // Falls back to soonest-first whenever the server could not score (see api/flights.js
    // #scoreQueue, which returns null rather than guessing). Ties and unscored rows still break
    // by date, so the order never becomes arbitrary.
    const byDate = (a, b) =>
      String(a.latestFinish || '9999-12-31').localeCompare(String(b.latestFinish || '9999-12-31'))
    return soon.sort((a, b) => {
      const sa = a.priority && a.priority.score
      const sb = b.priority && b.priority.score
      if (sa != null && sb != null && sa !== sb) return sb - sa
      if (sa != null && sb == null) return -1
      if (sb != null && sa == null) return 1
      return byDate(a, b)
    })
  }, [rawLineItems])

  // NOTHING HIDDEN WITHOUT A REASON (docs/OPERATING_MODEL.md rule 10, 2026-09-23). Every queued
  // milestone in scope that the line above leaves out, with the one reason it was left out, in
  // the order the filters apply: waiting on a predecessor, then (firm view) behind its project's
  // next milestone. Brandon could not find milestones he expected and nothing on screen said why.
  const hiddenItems = useMemo(() => {
    const shownIds = new Set(lineItems.map(m => m.milestoneId))
    const out = []
    for (const p of (projects || [])) {
      if (scope !== 'all' && p.projectId !== scope) continue
      for (const m of (p.milestones || [])) {
        if (shownIds.has(m.milestoneId)) continue
        let reason, detail
        if (m.blocked) { reason = 'waiting'; detail = (m.waitingOn && m.waitingOn.length) ? m.waitingOn.join(', ') : 'its predecessor' }
        else if (scope === 'all' && p.next && p.next.milestoneId !== m.milestoneId) { reason = 'behind'; detail = p.next.name }
        else continue
        out.push({ ...m, projectName: p.projectName, hiddenReason: reason, hiddenDetail: detail })
      }
    }
    return out
  }, [projects, scope, lineItems])

  // Reports the same two queues the gate stacks show (lineItems/queuedThreads) up to
  // FlightPlanTab, which renders them as an actual readable "Next up" list below "In the air" —
  // Matt: "We need the list at the top to show those in flight and then under that list another
  // list showing the next ones coming up for flight." The stacks stay (the visual gate), this
  // just gives the same underlying queue a second, readable form — one source of computation,
  // reported outward rather than duplicated.
  //
  // REPORT ONLY REAL CHANGES (2026-09-24). This effect calls setQueue in FlightPlanTab, which
  // re-renders it and therefore this chart; if any dependency here is a list rebuilt on every render
  // (the `threads` prop was — `(threadPlan.threads || []).map(...)`), the two components chase each
  // other forever: ~9 commits/s of identical output, the renderer at 100% CPU and growing to 1–2 GB
  // until Chrome killed the tab "Out of Memory". The source is fixed upstream (stable EMPTY lists); this
  // signature check is the belt to that suspender — a rebuilt-but-identical queue is not reported.
  const lastQueueSig = useRef('')
  useEffect(() => {
    if (!onQueueChange) return
    const sig = lineItems.map(m => m.milestoneId).join(',') + '|'
      + queuedThreads.map(th => th.threadId).join(',') + '|'
      + hiddenItems.map(m => m.milestoneId + ':' + m.hiddenReason + ':' + (m.hiddenDetail || '')).join(',')
    if (sig === lastQueueSig.current) return
    lastQueueSig.current = sig
    onQueueChange({ flights: lineItems, threads: queuedThreads, hidden: hiddenItems })
  }, [onQueueChange, lineItems, queuedThreads, hiddenItems])

  /* ── dragging ─────────────────────────────────────────────────────────────────────────── */

  const tFromClientX = useCallback((clientX) => {
    const el = chartRef.current
    if (!el) return 0.5
    const r = el.getBoundingClientRect()
    return Math.max(0, Math.min(1, (clientX - r.left - PAD.l) / innerW))
  }, [innerW, PAD.l])

  const idOf = (kind, item) => (kind === 'flight' || kind === 'squadron') ? item.flightId : kind === 'thread' ? item.threadId : item.milestoneId
  // Kinds that are already ON the curve (have a real, judged hillPosition to drag FROM) rather
  // than starting fresh from wherever the pointer landed — 'new' (a milestone lifted off the
  // flight deck) is the only kind excluded.
  const ON_CURVE_KINDS = ['flight', 'thread', 'squadron']

  const startDrag = useCallback((kind, item, e) => {
    if (readOnly) return   // the past is a record, not a thing to edit
    if ((kind === 'flight' || kind === 'squadron') && !isMyPlane(item)) return   // only its captain moves it
    // Don't let a fresh drag start on something still settling its last move — the note
    // prompt/save for the previous drop hasn't landed yet.
    if (ON_CURVE_KINDS.includes(kind) && pending && pending.kind === kind && pending.id === idOf(kind, item)) return
    e.preventDefault()
    const t = ON_CURVE_KINDS.includes(kind) ? item.hillPosition : tFromClientX(e.clientX)
    setDrag({ kind, id: idOf(kind, item), item, t })
  }, [tFromClientX, readOnly, pending])

  useEffect(() => {
    if (!drag) return
    const move = (e) => setDrag(d => d && ({ ...d, t: tFromClientX(e.clientX), live: true }))
    const up = (e) => {
      const t = tFromClientX(e.clientX)
      const el = chartRef.current
      const r = el && el.getBoundingClientRect()
      const inside = r && e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top - 40 && e.clientY <= r.bottom + 40
      const d = drag
      setDrag(null)
      if (!inside || !d) return
      // A QUEUED thread (still at its seed — never judged) dragged straight from the gate stack
      // to the landing zone is the tower controller's fast-track gesture (Matt: "simply drag it
      // from the flight line to the end... without the popup menu steps. Dragging it over just
      // feels like an accomplishment."). No move-note prompt (it isn't being judged, it's being
      // closed) and no confirm dialog — the green "Release to close this thread" pill shown
      // during the drag IS the warning, and a fast-track close is reversible via Reopen. An
      // IN-AIR thread dropped at the end keeps the deliberate path below (activity modal),
      // since that one may deserve a real closing note.
      if (d.kind === 'thread' && t >= LANDING_T && isSeedThread(d.item) && onFastTrackThread) {
        onFastTrackThread(d.item, { viaDrag: true, t })   // t: the landing animation hops from the drop point, not the seed
        return
      }
      if (ON_CURVE_KINDS.includes(d.kind)) {
        // Dropped all the way to the right: walk it through the SAME closeout the outline itself
        // uses, not a shortcut around it. Matt: "make sure that when dragging all the way to the
        // right, the close out action is triggered... use the act of dragging these from
        // departure to landing is a quick way to chug through getting caught up."
        //
        // Flights → CloseoutModal (open-closeout-modal), the outline's own "this is probably
        // done, let's actually close it" wizard (CT-025 reconcile) — it enforces the checklist
        // gate and then hands off to the real post flow itself, so this reuses the outline's
        // process rather than reimplementing it a third place.
        //
        // Threads → ThreadActivityModal preset to 'Thread Closed' (open-thread-activity-modal).
        // Threads have no checklist/post gate to enforce — closing IS the activity, automatically
        // recorded (services/threads.js#closeThread's Thread Closed activity + Closed checkbox in
        // one write) — so there is no separate readiness check to route through first; the modal
        // still requires a person to actually look at it and (optionally) write why before it
        // commits, which is the point, not a rubber stamp.
        //
        // Squadrons (2026-09-01) deliberately have NO closeout branch here — neither condition
        // below matches d.kind==='squadron', so dropping one past LANDING_T just moves it there;
        // disbanding stays an explicit menu action only (see OnCurveSquadron's own drag pill).
        const maybeCloseOut = () => {
          if (t < LANDING_T) return
          if (d.kind === 'flight' && d.item.milestoneId) {
            window.dispatchEvent(new CustomEvent('open-closeout-modal', {
              detail: { msId: d.item.milestoneId, name: d.item.name, projectId: d.item.projectId },
            }))
          } else if (d.kind === 'thread' && d.item.threadId) {
            window.dispatchEvent(new CustomEvent('open-thread-activity-modal', {
              detail: { threadId: d.item.threadId, threadTitle: d.item.title, presetType: 'Thread Closed' },
            }))
          }
        }
        if (Math.abs(t - d.item.hillPosition) > 0.005) {
          // Freeze the plane at the dropped point — through the note prompt AND the save — and
          // only release once the move's promise settles (it resolves after the reload, so the
          // fresh hillPosition already matches before this clears; nothing to snap to).
          setPending({ kind: d.kind, id: d.id, t })
          const mover = d.kind === 'flight' ? onMoveHill : d.kind === 'squadron' ? onMoveSquadronHill : onMoveThreadHill
          Promise.resolve(mover(d.item, t)).finally(() => {
            setPending(p => (p && p.kind === d.kind && p.id === d.id ? null : p))
            maybeCloseOut()
          })
        } else {
          maybeCloseOut()
        }
      } else {
        onDropNew(d.item, t)
      }
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', () => setDrag(null))
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
  }, [drag, tFromClientX, onMoveHill, onMoveThreadHill, onMoveSquadronHill, onDropNew, onFastTrackThread])

  // Held projects can't fly (see api/flights.js's own grounded/queue split) — no reason to offer
  // one as a scope to narrow into here. `projects` is already the non-held queue in the normal
  // case; `p.held` is checked anyway rather than trusted blindly, since the two hold sources
  // (Supabase project_holds vs. the mirrored contract_stage) don't always agree the instant a
  // project changes state, and this filter is cheap insurance against showing a stale one.
  const projectOptions = useMemo(
    () => (projects || []).filter(p => p.projectId && !p.held).map(p => ({ id: p.projectId, name: p.projectName || 'Unnamed' })),
    [projects])

  // Drilling into a specific project's own schedule — predecessors, dependencies, dragging
  // dates — is the interactive Gantt, not the outline's read-mostly Schedule section. Matt:
  // "a LOT of projects off track right now and drilling into a particular project's timeline to
  // make adjustments about predecessors... would be great." Reuses the exact event the outline's
  // own "Edit dates" / "Open Gantt" menu items already dispatch (GanttModal is mounted once,
  // globally, in OutlineView.jsx, so it hears this regardless of the Pulse workspace on top).
  const openSchedule = useCallback(() => {
    if (scope === 'all') return
    window.dispatchEvent(new CustomEvent('open-gantt-modal', { detail: { projectId: scope } }))
  }, [scope])

  return (
    <div style={S.wrap}>
      <PlaneDefs />

      {/* ft-head / ft-head-sub: compact-viewport (≤760px) overrides only — see the media block
          in this file's <style> below. Above the breakpoint no rule fires, so desktop rendering
          is untouched by the classNames. */}
      <div className="ft-head" style={S.head}>
        <span style={S.title}>Flight Board</span>
        <span className="ft-head-sub" style={S.sub}>each plane sits where its captain last reported it</span>
        <div style={{ flex: 1 }} />
        {/* Crew resource counts — Matt originally asked for the upper-right corner of the chart
            itself, but BOTH corners sit directly above a stack that can grow to the same max
            height (DECK_VISIBLE_CAP=12 items × STACK_GAP, on either side — the left queue deck
            and the right landed deck share the exact same sizing constants). The backfill-
            landing fix populated the right stack enough, for the first time, to actually reach
            that height and collide with it (2026-08-31: "the stacked planes and helicopters are
            overlapping the available crews summary"). Relocating into a corner would only move
            the same collision to whichever side eventually fills up. The header row is the one
            place with no stack beneath it at all. */}
        {/* Plain readout now, not a hover popover (2026-09-10 — see boardView's own comment).
            Clicking it jumps straight to the Pilots tab below, same destination the old hover
            card used to reveal in place. */}
        {(crewFlying != null || crewAvailable != null) && (
          <button
            type="button"
            style={{ ...S.crewStat, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            onClick={() => setBoardView('pilots')}
            title="View pilots"
          >
            {crewFlying != null && <span><b style={S.crewStatN}>{crewFlying}</b> in the air</span>}
            {crewFlying != null && crewAvailable != null && <span style={S.crewStatSep}>·</span>}
            {crewAvailable != null && <span><b style={{ ...S.crewStatN, color: crewAvailable > 0 ? PALETTE.green : PALETTE.amber }}>{crewAvailable}</b> available</span>}
          </button>
        )}
        {scope !== 'all' && (
          <button style={S.scheduleBtn} onClick={openSchedule} title="Open this project's interactive Gantt">
            Open schedule ↗
          </button>
        )}
        <ScopePicker scope={scope} setScope={setScope} options={projectOptions} />
        {onRefresh && (
          <button style={S.scheduleBtn} onClick={onRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </div>

      {/* ── board tabs — the flight curve and the pilots status table share this one footprint,
          switched with a click instead of the old hover popover (2026-09-10, see boardView's
          own comment above). */}
      <div style={S.boardTabs}>
        <button
          type="button"
          style={{ ...S.boardTab, ...(boardView === 'curve' ? S.boardTabActive : {}) }}
          onClick={() => setBoardView('curve')}
        >
          Flight Path
        </button>
        {canSeeCrews && (
          // Who flies with whom (database/344, 2026-10-25): manager and admin seats only.
          <button
            type="button"
            style={{ ...S.boardTab, ...(boardView === 'crews' ? S.boardTabActive : {}) }}
            onClick={() => setBoardView('crews')}
          >
            Crews
          </button>
        )}
        <button
          type="button"
          style={{ ...S.boardTab, ...(boardView === 'pilots' ? S.boardTabActive : {}) }}
          onClick={() => setBoardView('pilots')}
        >
          Pilots
        </button>
      </div>

      {boardView === 'crews' && canSeeCrews ? (
        <div style={{ ...S.chart, height: CHART_H, padding: 16, boxSizing: 'border-box', overflowY: 'auto' }}>
          <CrewPairsTab />
        </div>
      ) : boardView === 'pilots' ? (
        <div style={{ ...S.chart, height: CHART_H, padding: 16, boxSizing: 'border-box', overflowY: 'auto' }}>
          {(crewTable || []).length > 0
            ? <PilotFoldTicket rows={crewTable} foldable={false}
                assignableFlights={assignableFlights} onChangePriority={onChangeCrewPriority} onAssign={onAssignCrewToFlight}
                today={today} checklists={checklists}
                onOpenFlight={readOnly ? null : onOpenFlight} onCrew={readOnly ? null : onCrew} onLand={readOnly ? null : onLand} />
            : <div style={S.chartEmpty}>No crew data yet.</div>}
        </div>
      ) : (
      <>
      {/* ── hill ──────────────────────────────────────────────────────────────────────────── */}
      {/* ft-chart-scroll / ft-chart-inner (2026-09-01, the iPhone pass): on a compact viewport
          the chart keeps a real 640px canvas and scrolls sideways instead of crushing the hill
          into the ~180px left over after both 104px margins. CSS-only — above the breakpoint the
          wrapper has no overflow rule and the inner min-width never binds, so desktop layout and
          the ResizeObserver's measurements are unchanged. Dragging still works on touch (every
          icon sets touchAction:'none', so a drag that starts ON one never pans the container;
          panning starts on empty chart instead), and tFromClientX already reads the chart's live
          getBoundingClientRect, which accounts for the scroll offset. */}
      <div className="ft-chart-scroll">
      <div ref={chartRef} className="ft-chart-inner" style={{ ...S.chart, height: CHART_H, cursor: drag ? 'grabbing' : 'default' }}
        onPointerMove={readOnly ? undefined : onChartPointerMove} onPointerLeave={readOnly ? undefined : onChartPointerLeave}>
        {!readOnly && cluster && !drag && (
          <ClusterCard cluster={cluster} chartEl={chartRef.current} hoverId={hover} today={today}
            flights={cluster.ids.map(id => shownFlights.find(f => f.flightId === id)).filter(Boolean)} />
        )}
        {!readOnly && pinned && !drag && (() => {
          const fl = pinned.ids.map(id => shownFlights.find(f => f.flightId === id)).filter(Boolean)
          if (!fl.length) return null
          const sel = pinned.sel && fl.find(f => f.flightId === pinned.sel)
          if (sel) {
            return <HoverCard key={'pin:' + sel.flightId} f={sel} t={sel.hillPosition} today={today}
              anchorEl={document.querySelector(`[data-ft-icon="${sel.flightId}"]`)}
              cl={checklists && checklists[sel.milestoneId]} actions={actionsFor(sel)}
              onBack={fl.length > 1 ? () => setPinned(p => ({ ...p, sel: null })) : null} backCount={fl.length} />
          }
          const ys = pinned.ids.map(id => flightPts.get(id)).filter(Boolean)
          if (!ys.length) return null
          return <ClusterCard pinned cluster={{ ids: pinned.ids, x: Math.min(...ys.map(q => q.x)), y: ys[0].y }} chartEl={chartRef.current}
            hoverId={hover} today={today} flights={fl} onSelect={(id) => setPinned(p => ({ ...p, sel: id }))} />
        })()}
          <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
            <line x1={PAD.l} y1={baseY} x2={PAD.l + innerW} y2={baseY} stroke="var(--aj-border)" strokeWidth="0.5" />
            <line
              x1={pt(0.5).x} y1={PAD.t - 8} x2={pt(0.5).x} y2={baseY}
              stroke="var(--aj-border-mid)" strokeWidth="0.5" strokeDasharray="3 4" />
            {/* Dashed, permanently — the approach path is a projection, not a solid commitment.
                The entrance is opacity/rise rather than the old dash-offset "draws itself in"
                trick, since that technique needs the dasharray for the reveal math and would
                fight a dasharray that's also carrying the dashed VISUAL style. */}
            <path
              d={curvePath} fill="none" stroke="var(--aj-text-muted)" strokeWidth="1.25"
              strokeLinecap="round" strokeDasharray="5 7"
              style={{
                opacity: drawn ? 0.55 : 0,
                transform: drawn ? 'translateY(0)' : 'translateY(6px)',
                transition: reduceMotion() ? undefined : 'opacity 480ms ease, transform 480ms cubic-bezier(.22,.8,.3,1)',
              }} />
            {drag && (drag.kind === 'flight' || drag.kind === 'new') && (
              <circle cx={pt(drag.t).x} cy={pt(drag.t).y} r="17" fill="var(--aj-accent-blue)" opacity="0.10" />
            )}

            {/* ── the threads curve — same shape, same horizontal mapping and ground line
                (baseY, not redrawn — it's the same line as the milestone curve's own), smaller
                amplitude. Same stroke weight and opacity as the milestone curve now (Matt: "the
                curve for the helicopters is lighter in color than the airplane curve") — only
                the dash RHYTHM ("4 6" vs "5 7") still tells the two apart. ── */}
            <path
              d={showThreads ? curvePath2 : ''} fill="none" stroke="var(--aj-text-muted)" strokeWidth="1.25"
              strokeLinecap="round" strokeDasharray="4 6"
              style={{
                opacity: drawn ? 0.55 : 0,
                transform: drawn ? 'translateY(0)' : 'translateY(6px)',
                transition: reduceMotion() ? undefined : 'opacity 480ms ease 80ms, transform 480ms cubic-bezier(.22,.8,.3,1) 80ms',
              }} />
            {drag && drag.kind === 'thread' && (
              <circle cx={pt2(drag.t).x} cy={pt2(drag.t).y} r="13" fill="var(--aj-accent-blue)" opacity="0.10" />
            )}
          </svg>

          <div style={{ ...S.zone, left: PAD.l, width: innerW / 2, top: baseY + 22 }}>Plotting the course</div>
          <div style={{ ...S.zone, left: PAD.l + innerW / 2, width: innerW / 2, top: baseY + 22 }}>Flying the course</div>

          {/* Hovering any plane on either curve shows what it's aiming for at the curve's own
              landing point, not just inside the hover card that follows the plane itself — the
              gap between "where it is" and "where it's headed" is the thing worth seeing.
              Flights and threads share the same t=1 ground point (both curves' hill(1)=0 at the
              same baseY/x), so one shared marker slot covers either. */}
          {(() => {
            const hf = hover ? shown.find(x => x.flightId === hover) : null
            const ht = !hf && threadHover ? scopedThreads.find(x => x.threadId === threadHover) : null
            if (!hf && !ht) return null
            const end = pt(1)
            const date = hf ? hf.latestFinish : ht.targetDeadline
            return (
              <div style={{ position: 'absolute', left: end.x, top: end.y, transform: 'translate(-100%, -22px)', ...S.endMark }}>
                {hf ? 'Target arrival' : 'Target'} · {date ? fmtDay(date) : 'undated'}
              </div>
            )
          })()}

          {!readOnly && (
            <div style={{ position: 'absolute', left: 16, bottom: CHART_H - baseY, zIndex: 45, display: 'flex', alignItems: 'flex-end', gap: DECK_GAP }}>
              <FlightDeck
                items={lineItems} today={today} overlayOpen={overlayOpen}
                onStartDrag={(m, e) => startDrag('new', m, e)}
                onOpen={onOpenFlight}
                onPutInAir={(m) => onDropNew && onDropNew(m, TAKEOFF_T)} />
              <ThreadDeck
                items={showThreads ? queuedThreads : []} today={today} overlayOpen={overlayOpen}
                onStartDrag={(th, e) => startDrag('thread', th, e)}
                onOpen={onOpenThread}
                onPutInAir={(th) => onMoveThreadHill && onMoveThreadHill(th, TAKEOFF_T)}
                onFastTrack={onFastTrackThread} />
            </div>
          )}
          {!readOnly && (
            <LandedStacks
              flights={scopedLanded} threads={showThreads ? scopedLandedThreads : []}
              groundGap={CHART_H - baseY}
              landing={landAnim} overlayOpen={overlayOpen}
              onOpenFlight={onOpenLanded} onOpenThread={onOpenLandedThread} />
          )}

          {/* planes already in the air — or where they were, when scrubbed back.
              STRUCTURE: the outer node anchors at the point on the curve and owns the
              left/top glide. The icon is a SEPARATE inner child that alone gets
              translate(-50%,-50%) — its own geometric center, not the icon+label bundle's
              combined center — landing exactly on the curve. The tag hangs at a fixed offset
              below that, so label height never pulls the icon off the line. */}
          {shownFlights.map(f => {
            if (landAnim && landAnim.kind === 'flight' && landAnim.id === f.flightId) return null   // flying the landing animation instead
            const dragging = drag && drag.kind === 'flight' && drag.id === f.flightId
            const settling = !dragging && pending && pending.kind === 'flight' && pending.id === f.flightId
            const t = dragging ? drag.t : settling ? pending.t : f.hillPosition
            const p0 = pt(t)
            // Stacked offset for whatever's sharing this spot — never applied to the one
            // actively being dragged, which must track the cursor exactly.
            const off = !dragging ? flightDeclutter.offsets.get(f.flightId) : null
            if (off && off.skip) return null   // past the visible-stack cap — counted in the overflow badge instead
            const p = off ? { x: p0.x + off.dx, y: p0.y + off.dy } : p0
            return (
              <OnCurveFlight
                key={f.flightId} f={f} p={p} t={t} dragging={dragging} off={off}
                isHover={hover === f.flightId} externalHover={hoverRowId != null && hoverRowId === f.flightId}
                inCluster={!!(hoverGroup && hoverGroup.length > 1 && hoverGroup.includes(f.flightId))}
                quiet={!!pinned} pinnedHere={!!(pinned && pinned.ids.includes(f.flightId))} onPin={pinPlane}
                busy={busy} today={today} checklists={checklists}
                readOnly={readOnly}
                onStartDrag={startDrag}
                onHoverIn={readOnly ? () => setHover(f.flightId) : null}
                onHoverOut={() => setHover(h => (h === f.flightId ? null : h))}
                onOpenFlight={onOpenFlight} onCrew={onCrew} onLand={onLand}
              />
            )
          })}
          {flightDeclutter.overflow.map((o, i) => (
            <OverflowBadge key={'fo' + i} p={pt(o.t)} count={o.count} />
          ))}

          {/* Squadrons (database/202_squadrons.sql) — bundles of up to 5 threads, crewed as one
              unit. They're `flights` rows (kind:'squadron') but live on the THREADS curve (pt2),
              since a squadron is conceptually a bundle of threads, not a milestone. Drag + a
              dedicated declutter pass added 2026-09-01 — see squadronDeclutter's own comment. */}
          {!readOnly && showThreads && shownSquadrons.map(f => {
            if (landAnim && landAnim.kind === 'squadron' && landAnim.id === f.flightId) return null
            const dragging = drag && drag.kind === 'squadron' && drag.id === f.flightId
            const settling = !dragging && pending && pending.kind === 'squadron' && pending.id === f.flightId
            const t = dragging ? drag.t : settling ? pending.t : f.hillPosition
            const p0 = pt2(t)
            const off = !dragging ? squadronDeclutter.offsets.get(f.flightId) : null
            if (off && off.skip) return null   // past the visible-stack cap — counted in the overflow badge instead
            const p = off ? { x: p0.x + off.dx, y: p0.y + off.dy } : p0
            return (
              <OnCurveSquadron
                key={f.flightId} f={f} p={p} t={t} dragging={dragging} off={off}
                isHover={hover === f.flightId} externalHover={hoverRowId != null && hoverRowId === f.flightId}
                today={today}
                onStartDrag={startDrag}
                onHoverIn={() => setHover(f.flightId)}
                onHoverOut={() => setHover(h => (h === f.flightId ? null : h))}
                onCrew={onCrew} onLand={onLand} onRemoveThread={onRemoveSquadronThread}
                onAddThread={onAddSquadronThread} onOpenThread={onOpenThread}
              />
            )
          })}
          {squadronDeclutter.overflow.map((o, i) => (
            <OverflowBadge key={'so' + i} p={pt2(o.t)} count={o.count} color={PALETTE.purple} />
          ))}

          {/* ── the threads curve's own planes ─────────────────────────────────────────────
              Same anchor/centering structure as the flights above, on pt2 instead of pt. No
              "past day" replay yet (see docs/FLIGHT_PLAN.md) — hidden while scrubbing rather
              than showing live positions under a day label that isn't really theirs. */}
          {!readOnly && showThreads && scopedThreads.map(th => {
            if (landAnim && landAnim.kind === 'thread' && landAnim.id === th.threadId) return null   // flying the landing animation instead
            const dragging = drag && drag.kind === 'thread' && drag.id === th.threadId
            const settling = !dragging && pending && pending.kind === 'thread' && pending.id === th.threadId
            // Still waiting at the gate (see queuedThreads' own comment) — lives in ThreadDeck
            // instead, unless it's actively being dragged off that stack or settling into its
            // first real judged position, in which case it needs to render here to follow.
            if (!dragging && !settling && isSeedThread(th)) return null
            const t = dragging ? drag.t : settling ? pending.t : th.hillPosition
            const p0 = pt2(t)
            const off = !dragging ? threadDeclutter.offsets.get(th.threadId) : null
            if (off && off.skip) return null   // past the visible-stack cap — counted in the overflow badge instead
            const p = off ? { x: p0.x + off.dx, y: p0.y + off.dy } : p0
            return (
              <OnCurveThread
                key={th.threadId} th={th} p={p} t={t} dragging={dragging} off={off}
                isHover={threadHover === th.threadId} externalHover={hoverRowId != null && hoverRowId === th.threadId}
                today={today}
                onStartDrag={startDrag}
                onHoverIn={() => setThreadHover(th.threadId)}
                onHoverOut={() => setThreadHover(h => (h === th.threadId ? null : h))}
                onOpenThread={onOpenThread} onPull={onPull} onFastTrack={onFastTrackThread}
                onCrew={onCrewThread}
              />
            )
          })}
          {!readOnly && showThreads && threadDeclutter.overflow.map((o, i) => (
            <OverflowBadge key={'to' + i} p={pt2(o.t)} count={o.count} color={PALETTE.teal} />
          ))}

          {/* the plane being lifted off the flight deck, following the cursor — same
              icon-centers-on-anchor structure as above, just always at full drag emphasis. */}
          {drag && drag.kind === 'new' && (
            <div style={{ position: 'absolute', left: pt(drag.t).x, top: pt(drag.t).y, zIndex: 40, pointerEvents: 'none' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-50%,-50%) scale(1.25)', filter: 'drop-shadow(0 8px 12px rgba(7,39,45,0.3))' }}>
                <Plane deg={attitudeFor(drag.t)} color={PALETTE.green} size={26} spin={false} />
              </div>
              <span style={{ ...S.tag, ...S.tagOn, position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, 16px)' }}>
                {drag.item.name}
              </span>
            </div>
          )}

          {/* the landing flight-over — a fast-tracked item flying the rest of its curve to the
              landing zone in green, then banking off the curve's end into the landed stack's
              bottom slot (shrinking to stack size, leveling out) and sitting there until the
              real item swaps in. See landAnim's own comment near the top of this component. */}
          {landAnim && !readOnly && (() => {
            const isTh = landAnim.kind === 'thread'
            const flySize = isTh ? 22 : 26
            const iconH = Math.round(DECK_ICON * (isTh ? 135 / 110 : 60 / 82))
            // The stack's bottom slot, in chart coordinates: LandedStacks anchors right:16 with
            // the heli stack one box+gap inside the plane stack; icons are right-aligned in
            // their DECK_BOX_W box and sit on the ground line (baseY).
            const dock = {
              x: size.w - 16 - (isTh ? DECK_BOX_W + DECK_GAP : 0) - DECK_ICON / 2 - 2,
              y: baseY - iconH / 2 - 2,
            }
            let x, y, deg, sz
            if (landAnim.phase === 'fly') {
              const gp = isTh ? pt2(landAnim.t) : pt(landAnim.t)
              x = gp.x; y = gp.y
              deg = isTh ? threadAttitudeFor(landAnim.t) : attitudeFor(landAnim.t)
              sz = flySize
            } else {
              const p = landAnim.phase === 'settled' ? 1 : landAnim.p
              const end = isTh ? pt2(1) : pt(1)
              x = end.x + (dock.x - end.x) * p
              y = end.y + (dock.y - end.y) * p
              deg = (isTh ? threadAttitudeFor(1) : attitudeFor(1)) * (1 - p)   // level out into the slot
              sz = flySize + (DECK_ICON - flySize) * p                          // shrink to stack size
            }
            return (
              <div style={{ position: 'absolute', left: x, top: y, zIndex: 48, pointerEvents: 'none' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-50%,-50%)', filter: landAnim.phase === 'fly' ? 'drop-shadow(0 6px 10px rgba(7,39,45,0.28))' : undefined }}>
                  <Plane deg={deg} icon={isTh ? 'heli' : undefined} color={PALETTE.green} size={sz} spin={false} />
                </div>
              </div>
            )
          })()}

          {shown.length === 0 && !drag && (
            <div style={S.chartEmpty}>
              {readOnly ? 'Nothing was in the air on this day.' : 'Drag a plane from the stack at the gate to put it in the air.'}
            </div>
          )}
      </div>
      </div>{/* /ft-chart-scroll */}
      </>
      )}

      {/* ── history scrubber ─────────────────────────────────────────────────────────────── */}
      {days.length > 0 && (
        <div style={S.scrub}>
          <button
            style={S.scrubBtn} disabled={dayIdx === 0}
            onClick={() => setDayIdx(i => (i == null ? days.length - 1 : Math.max(0, i - 1)))}
            title="Earlier"
          >‹</button>

          <div style={S.scrubMid}>
            <span style={S.scrubDay}>
              {readOnly ? fmtLongDay(days[dayIdx]) : 'Live'}
            </span>
            <span style={S.scrubMeta}>
              {readOnly
                ? `${shown.filter(f => f.movedToday).length} moved · ${dayIdx + 1}/${days.length}`
                : `${days.length} day${days.length === 1 ? '' : 's'} of movement recorded`}
            </span>
          </div>

          {/* Ticks, one per day something moved. Density itself is a signal: long gaps between
              ticks are weeks when nobody committed a view. */}
          <div style={S.ticks}>
            {days.map((d, i) => (
              <button
                key={d} title={fmtLongDay(d)}
                onClick={() => setDayIdx(i)}
                style={{
                  ...S.tick,
                  background: (dayIdx == null || i <= dayIdx) ? 'var(--aj-text-muted)' : 'var(--aj-border-mid)',
                  opacity: dayIdx === i ? 1 : 0.5,
                  height: dayIdx === i ? 16 : 10,
                }} />
            ))}
          </div>

          <button
            style={S.scrubBtn} disabled={!readOnly}
            onClick={() => setDayIdx(i => (i == null || i >= days.length - 1 ? null : i + 1))}
            title="Later"
          >›</button>

          {readOnly && (
            <button style={S.liveBtn} onClick={() => setDayIdx(null)}>Back to live</button>
          )}
        </div>
      )}

      {/* What was actually said that day. The note is the half a snapshot could never keep. */}
      {readOnly && (
        <div style={S.notes}>
          {shown.filter(f => f.movedToday && f.note).length === 0
            ? <div style={S.noteEmpty}>Planes moved, but nobody wrote down why.</div>
            : shown.filter(f => f.movedToday && f.note).map(f => (
                <div key={f.flightId} style={S.note}>
                  <span style={S.noteNo}>{flightTag(f)}</span>
                  <span style={S.noteTxt}>{f.note}</span>
                </div>
              ))}
        </div>
      )}

      <style>{`
        @keyframes ft-in { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
        @keyframes ft-stall { 0%,100% { opacity: .25; transform: scale(1) } 50% { opacity: .05; transform: scale(1.5) } }
        /* Idle "alive" bob (2026-08-31, Matt: "very subtle... just feeling alive"). A plane
           glides — one smooth sine, slow. A helicopter hovers on rotor vibration — a faster,
           asymmetric 4-step wobble reads as unsteady rather than mechanical, which is the
           actual difference between the two aircraft, not just a speed change on the same
           curve. Both amplitudes are deliberately tiny (≤2px / ≤1.5°) — a static screen, not
           a game, so this must read as alive without ever reading as distracting. */
        @keyframes ft-plane-bob { 0%, 100% { transform: translateY(0) rotate(0deg) } 50% { transform: translateY(-1.5px) rotate(1deg) } }
        .ft-cluster-row:hover { background: var(--aj-bg-subtle) !important; }
        /* Slowed 0.9s -> 1.7s and given more rotation swing (2026-08-31, Matt: "juggles up and
           down a little too fast... add a slight wobble") — same 4-stop asymmetric shape, just
           a wider, slower sway so it reads as an unsteady hover rather than a jitter.
           (This note briefly shipped as a JSX-style comment INSIDE this template literal, which
           emitted the literal braces into the CSS — an invalid anonymous block browsers happened
           to error-recover past. Plain CSS comment now; nothing but comment syntax changed.) */
        @keyframes ft-heli-bob { 0%, 100% { transform: translateY(0) rotate(0deg) } 22% { transform: translateY(-1.8px) rotate(-2.5deg) } 48% { transform: translateY(0.8px) rotate(1.8deg) } 74% { transform: translateY(-1.2px) rotate(-2deg) } }

        /* ── Compact viewports (iPhone, iPad split view) — 2026-09-01, Matt: "review the way the
           flight plan will look and work on iphone... keeping the desktop appearance and
           functionality exactly the same." Everything below fires ONLY under 760px, so desktop
           rendering is byte-identical; !important is required because it's overriding the
           components' own inline styles. */
        @media (max-width: 760px) {
          /* Header controls wrap into rows instead of shoving the scope picker off-screen; the
             one-line tagline goes — it's instruction, not control, and the space is scarce. */
          .ft-head { flex-wrap: wrap !important; row-gap: 8px !important; }
          .ft-head-sub { display: none !important; }
          /* The hill keeps a real 640px canvas and pans sideways — see the wrapper's own comment
             at the chart div. */
          .ft-chart-scroll { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
          .ft-chart-inner { min-width: 640px !important; }
          /* Scope picker flexes into whatever row space is left rather than a fixed 220px. */
          .ft-scope { flex: 1 1 140px !important; min-width: 120px; }
          .ft-scope input { width: 100% !important; }
        }
      `}</style>
    </div>
  )
}

/* ── bits ────────────────────────────────────────────────────────────────────────────────── */

/**
 * The project-scope picker — Matt: "Make the filter dropdown searchable." A plain native
 * `<select>` doesn't let you type to jump straight to a project; this is a small combobox
 * instead — text input + a filtered list — mirroring the same searchable-picker pattern
 * already established elsewhere (`ScheduleIntelWorkspace.jsx`'s own `ProjectPicker`), adapted
 * to commit to a project ID (for `scope`) rather than free text, since `projectOptions` is
 * already loaded data here (no lazy fetch needed).
 *
 * Closed: shows the current selection's name ("Whole firm" or the picked project). Opening
 * clears the field to blank (with the current selection as a placeholder hint) so typing can
 * start immediately without deleting anything first. Selecting an option — or blurring without
 * picking one — always resolves back to a valid `scope`; there's no way to leave it on an
 * unmatched typed string.
 */

/** A stable per-id animation delay (0..period seconds) — not Math.random(), so the same item
 * always gets the same phase across re-renders, and a whole stack of planes/helis doesn't bob
 * in visible unison. Used by the idle-bob animation only; nothing depends on the exact value. */
function bobDelay(id, periodSec) {
  let h = 0
  const s = String(id || '')
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return ((Math.abs(h) % 1000) / 1000) * periodSec
}

// Column widths, folded vs unfolded — folded shows just enough to scan who's flying what;
// unfolded adds the judged status word, arrival date, and delay. Matches the design's own
// COLLAPSED/EXPANDED split (Pilot Ticket View.dc.html) — the Status column goes from an
// icon-only sliver to a real labeled column, Arrival/Delays go from 0 (out of the DOM's visible
// flow) to their full width.
const TICKET_COLLAPSED_W = [108, 28, 106]
const TICKET_EXPANDED_W = [108, 104, 92, 122, 78]
const TICKET_TRANSITION = 'grid-template-columns 0.34s cubic-bezier(0.22, 1, 0.36, 1)'

/**
 * The folded flight ticket. Matt's design spec, "Fold Interaction Reference/Demo": a 5-column
 * table (Pilots / Status / Flight no. / Arrival / Delays) that starts FOLDED to just the first
 * 3 columns (Status collapsed to an icon-only sliver, Arrival/Delays width 0), and unfolds via a
 * click on the right-edge "torn ticket" tab — all 5 columns always render, in both states; only
 * their WIDTH (animated via a `grid-template-columns` transition) and content opacity change, so
 * there's one table, not a swap between two different layouts. Every pilot gets exactly 2 slot
 * rows within their one name-cell's height, matching the real 2-simultaneous-assignment cap
 * (database/204_crew_two_and_thread_crew.sql) — a pilot keeps the same row height whether both,
 * one, or neither slot is filled.
 *
 * The status icon reuses this file's own `Plane` component (not the mockup's own icon glyph) —
 * visual consistency with every other flight/thread icon on this board mattered more than
 * pixel-fidelity to the static design.
 *
 * `foldable` (2026-09-10, default true): the fold/tooth edge exists to compress this table into
 * a small hover popover's worth of space. The Pilots TAB (FlightTracker's own boardView) gives
 * it the whole chart footprint instead, so there's nothing to fold for — pass `foldable={false}`
 * to force the table permanently expanded and hide the tooth strip.
 */
/**
 * A flying seat's flight number, hoverable for the SAME popup the curve's own icon shows — Matt,
 * 2026-09-10: "can I get the same popup info that shows on the curve, please?" `slot.flight`/
 * `slot.thread` are the exact objects `describeAssignment` (FlightPlanTab.jsx) already resolved
 * off `flightById`/`threadById` — this renders whichever of the curve's three hover cards
 * (HoverCard/SquadronHoverCard/ThreadHoverCard) that same kind uses elsewhere, so the two are
 * never two different implementations to keep in sync. Hover state + anchor ref are local, same
 * pattern as OnCurveFlight/OnCurveThread/OnCurveSquadron use for their own hover cards.
 */
function SlotTag({ slot, today, checklists, onOpenFlight, onCrew, onLand }) {
  const [hover, setHover] = useState(false)
  const ref = useRef(null)
  const f = slot.flight
  const cl = (f && slot.kind === 'milestone') ? (checklists && checklists[f.milestoneId]) : null
  // Same upgrade as the curve's own hover card (Matt, 2026-09-10: "do the same upgrade to the
  // pilots view"): a grace timer so the mouse can travel from the flight number down onto the
  // card, and the five actions on the card. The card reports its own enter/leave through hold/release.
  const leaveTimer = useRef(null)
  const hold = () => { if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null } setHover(true) }
  const release = () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); leaveTimer.current = setTimeout(() => { leaveTimer.current = null; setHover(false) }, 280) }
  useEffect(() => () => { if (leaveTimer.current) clearTimeout(leaveTimer.current) }, [])
  const actions = (f && slot.kind === 'milestone' && (onOpenFlight || onCrew || onLand)) ? {
    open:   () => { setHover(false); onOpenFlight && onOpenFlight(f) },
    crew:   () => { setHover(false); onCrew && onCrew(f) },
    land:   () => { setHover(false); onLand && onLand(f, 'posted') },
    pull:   () => { setHover(false); onLand && onLand(f, 'released') },
    cancel: () => { setHover(false); onLand && onLand(f, 'cancelled') },
  } : null
  return (
    <span
      ref={ref}
      onPointerEnter={hold}
      onPointerLeave={release}
      style={{ overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default' }}
    >
      {slot.tag}
      {hover && f && slot.kind === 'squadron' && (
        <SquadronHoverCard f={f} t={f.hillPosition} today={today} anchorEl={ref.current} />
      )}
      {hover && slot.thread && slot.kind === 'thread' && (
        <ThreadHoverCard t={slot.thread} pos={slot.thread.hillPosition} today={today} anchorEl={ref.current} />
      )}
      {hover && f && slot.kind === 'milestone' && (
        <HoverCard f={f} t={f.hillPosition} today={today} anchorEl={ref.current} cl={cl}
          actions={actions} onHold={hold} onRelease={release} />
      )}
    </span>
  )
}
function PilotFoldTicket({ rows, foldable = true, assignableFlights, onChangePriority, onAssign, today, checklists, onOpenFlight, onCrew, onLand }) {
  const [openState, setOpenState] = useState(false)
  const open = foldable ? openState : true
  const widths = open ? TICKET_EXPANDED_W : TICKET_COLLAPSED_W
  // Fixed pixel columns are for the folding ticket in its popover-sized home. On the Pilots TAB
  // (foldable=false) the table owns the whole chart footprint, so the columns share the width.
  const template = foldable ? widths.map(w => w + 'px').join(' ') : 'minmax(160px, 1.6fr) 1.2fr 1fr 1fr 1fr'
  const detailOpacity = open ? 1 : 0

  const totals = useMemo(() => {
    const t = { grounded: 0, inAir: 0, onTime: 0, delayed: 0 }
    for (const r of rows) for (const s of r.slots) {
      if (!s.flying) { t.grounded++; continue }
      t.inAir++
      if (s.late) t.delayed++; else t.onTime++
    }
    return t
  }, [rows])

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', width: foldable ? undefined : '100%' }}>
      <div style={foldable ? S.ticketBase : { ...S.ticketBase, borderRadius: 8, flex: '1 1 auto', width: '100%', border: '0.5px solid var(--aj-border)' }}>
        <div style={{ ...S.ticketGrid, gridTemplateColumns: template, background: 'var(--aj-bg-subtle)', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={S.ticketHeadCell}>Pilots</div>
          <div style={S.ticketHeadCell}>Status</div>
          <div style={S.ticketHeadCell}>Flight no.</div>
          <div style={{ ...S.ticketHeadCell, opacity: detailOpacity, transition: 'opacity 0.2s ease' }}>Arrival</div>
          <div style={{ ...S.ticketHeadCell, opacity: detailOpacity, transition: 'opacity 0.2s ease' }}>Delays</div>
        </div>

        {rows.map((row, ri) => (
          <div key={row.memberId} style={{ ...S.ticketGrid, gridTemplateColumns: template, background: ri % 2 ? '#fff' : 'var(--aj-bg-page)', borderBottom: '0.5px solid var(--aj-border)' }}>
            <div style={S.ticketNameCell}>{row.name}</div>
            {/* Status — the icon alone reads it folded (red/green/gray, same convention every
                other flight/thread icon on this board already uses); the word joins once
                unfolded, so the two views literally agree, per the design's own spec. */}
            <div style={S.ticketSlotCol}>
              {row.slots.map((slot, si) => (
                <div key={si} style={{ ...S.ticketSlot, gap: 6 }}>
                  <span style={{ flexShrink: 0, display: 'inline-flex' }}>
                    <Plane deg={0} spin={false} color={!slot.flying ? 'var(--aj-text-muted)' : (slot.late ? PALETTE.red : PALETTE.green)} size={13} />
                  </span>
                  <span style={{ fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: open ? (foldable ? 120 : 'none') : 0, opacity: detailOpacity, transition: 'max-width 0.24s ease, opacity 0.2s ease', color: !slot.flying ? 'var(--aj-text-muted)' : (slot.late ? PALETTE.red : PALETTE.green) }}>
                    {slot.flying ? slot.status : 'Grounded'}
                  </span>
                </div>
              ))}
            </div>
            <div style={S.ticketSlotCol}>
              {row.slots.map((slot, si) => {
                if (slot.flying) {
                  const rank = slot.priority === 1 ? '1st' : slot.priority === 2 ? '2nd' : null
                  return (
                    <div key={si} style={{ ...S.ticketSlot, gap: 6, fontFamily: '"DM Mono", monospace', fontSize: 11.5, fontWeight: 500, color: 'var(--aj-text)' }}>
                      <SlotTag slot={slot} today={today} checklists={checklists} onOpenFlight={onOpenFlight} onCrew={onCrew} onLand={onLand} />
                      {/* Their own rank on THIS seat — click swaps it with their other open seat
                          (server-side claimPriority), matching the CrewPicker's own radio pair.
                          Matt, 2026-09-10: "I want to see their first and second priorities and
                          be able to change them from the pilots list." */}
                      {rank && onChangePriority && (
                        <button
                          type="button"
                          onClick={() => onChangePriority(row.memberId, slot.flightId, slot.priority === 1 ? 2 : 1)}
                          title={`Their ${rank === '1st' ? 'first' : 'second'}-priority plane — click to make it their ${rank === '1st' ? 'second' : 'first'}`}
                          style={S.pilotRankBtn}
                        >{rank}</button>
                      )}
                    </div>
                  )
                }
                // Grounded slot — offer a straight-from-the-list assign onto any flight with an
                // open seat (2026-09-10 ask), excluding whatever this pilot already flies so the
                // list never offers a duplicate.
                const takenIds = new Set(row.slots.filter(s => s.flying).map(s => s.flightId))
                const options = (assignableFlights || []).filter(f => !takenIds.has(f.flightId))
                if (!onAssign || options.length === 0) {
                  return (
                    <div key={si} style={{ ...S.ticketSlot, fontFamily: '"DM Mono", monospace', fontSize: 11.5, fontWeight: 500, color: 'var(--aj-text-muted)' }}>—</div>
                  )
                }
                return (
                  <div key={si} style={S.ticketSlot}>
                    <select
                      value=""
                      onChange={e => { const fid = e.target.value; if (fid) onAssign(row.memberId, row.name, fid) }}
                      style={S.pilotAddSelect}
                      title="Seat this pilot on a flight with an open slot"
                    >
                      <option value="">+ Add to flight…</option>
                      {options.map(f => <option key={f.flightId} value={f.flightId}>{f.label}</option>)}
                    </select>
                  </div>
                )
              })}
            </div>
            <div style={{ ...S.ticketSlotCol, opacity: detailOpacity, transition: 'opacity 0.24s ease' }}>
              {row.slots.map((slot, si) => (
                <div key={si} style={{ ...S.ticketSlot, fontSize: 11, color: 'var(--aj-text-mid)' }}>
                  {slot.flying && slot.due ? fmtDay(slot.due) : '—'}
                </div>
              ))}
            </div>
            <div style={{ ...S.ticketSlotCol, opacity: detailOpacity, transition: 'opacity 0.24s ease' }}>
              {row.slots.map((slot, si) => (
                <div key={si} style={{ ...S.ticketSlot, fontFamily: '"DM Mono", monospace', fontSize: 11, fontWeight: 600, color: !slot.flying ? 'var(--aj-text-muted)' : (slot.late ? PALETTE.red : PALETTE.green) }}>
                  {slot.flying ? (slot.delay || '—') : '—'}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ ...S.ticketGrid, gridTemplateColumns: template, background: 'var(--aj-bg-subtle)' }}>
          <div style={S.ticketHeadCell}>Totals</div>
          <div />
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 4px 9px 6px', fontSize: 11, color: 'var(--aj-text-muted)', whiteSpace: 'nowrap' }}>
            <b style={{ fontFamily: '"DM Mono", monospace', fontWeight: 600, color: 'var(--aj-text)' }}>{totals.grounded}</b> grounded
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 6px', fontSize: 11, color: 'var(--aj-text-mid)', whiteSpace: 'nowrap', opacity: detailOpacity, transition: 'opacity 0.24s ease' }}>
            <b style={{ fontFamily: '"DM Mono", monospace', fontWeight: 600 }}>{totals.inAir}</b> in the air
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 6px', fontSize: 11, color: PALETTE.green, whiteSpace: 'nowrap', opacity: detailOpacity, transition: 'opacity 0.24s ease' }}>
            <b style={{ fontFamily: '"DM Mono", monospace', fontWeight: 600 }}>{totals.onTime}</b> on time
          </div>
        </div>
      </div>

      {/* The fold edge — a torn-ticket perforation, per the design spec. The whole strip is the
          button (16px hit target, full height); clicking toggles fold state. Teeth are a plain
          CSS border-triangle repeated down the strip. TWO rounds of "make the color contrast
          stronger" (--aj-bg-subtle/elevated, then --aj-bg-muted/page) still read as "too light"
          live (Matt, 2026-09-02, twice) -- a subtle color illusion is the wrong mechanism for a
          primary control with no other UI cue. Now backed by an unambiguous chevron icon that
          doesn't depend on color contrast being just-right, plus a solid (not pastel) tinted
          strip via --aj-border-mid as a real background fill. Only rendered when `foldable` —
          the Pilots tab has the whole chart footprint and nothing to fold for. */}
      {foldable && (
        <div
          onClick={() => setOpenState(o => !o)}
          title={open ? 'Click to fold' : 'Click to unfold — status, arrival, delays'}
          style={{ ...S.ticketEdge, position: 'relative' }}
        >
          {Array.from({ length: 24 }, (_, i) => (
            <span key={i} style={S.ticketTooth} />
          ))}
          <div style={S.ticketEdgeChevron}>
            <svg width="7" height="11" viewBox="0 0 7 11" fill="none">
              <path
                d={open ? 'M5.5 1L1.5 5.5L5.5 10' : 'M1.5 1L5.5 5.5L1.5 10'}
                stroke="var(--aj-text-mid)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  )
}
function ScopePicker({ scope, setScope, options }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const current = scope === 'all' ? 'Whole firm' : ((options.find(o => o.id === scope) || {}).name || 'Whole firm')
  const ql = query.trim().toLowerCase()
  const showWholeFirm = !ql || 'whole firm'.includes(ql)
  const matches = !ql ? options : options.filter(o => o.name.toLowerCase().includes(ql))

  const pick = (id) => { setScope(id); setOpen(false); setQuery('') }

  return (
    // ft-scope: compact-viewport-only sizing (the input's fixed 220px width would eat most of a
    // phone row) — see the media block in this file's <style>. No effect above the breakpoint.
    <div className="ft-scope" style={{ position: 'relative' }}>
      <input
        style={S.select}
        value={open ? query : current}
        placeholder={open ? current : undefined}
        onFocus={() => { setQuery(''); setOpen(true) }}
        onChange={e => setQuery(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={e => { if (e.key === 'Escape') e.currentTarget.blur() }}
      />
      {open && (
        <div style={S.scopeMenu}>
          {showWholeFirm && (
            <button style={S.scopeItem} onMouseDown={e => { e.preventDefault(); pick('all') }}>
              Whole firm
            </button>
          )}
          {matches.length === 0 && ql && <div style={S.scopeEmpty}>No match</div>}
          {matches.slice(0, 40).map(o => (
            <button key={o.id} style={S.scopeItem} onMouseDown={e => { e.preventDefault(); pick(o.id) }}>
              {o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const toDate = (s) => (s ? new Date(s + 'T00:00:00') : null)
const fmtDay = (s) => {
  const d = toDate(s)
  return d ? d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : '—'
}

/**
 * A 4-letter project code, derived purely for display — never stored, never part of a flight's
 * own identity. Matt originally asked for a 3-letter word-initials code ("update flight numbers
 * to have a project prefix... that help identify the project in the flight tracker at a
 * glance"), then 2026-09-03 asked to change it to a literal first-four-letters read instead.
 * Flight numbers themselves stay exactly as minted (CLAUDE.md: "identity, minted once, never
 * recomputed") — this is computed fresh from the project name every render and shown ALONGSIDE
 * it, so it works for flights that already flew under a plain number, costs no migration, and
 * simply updates if a project is later renamed.
 *
 * Non-letters (spaces, digits, punctuation) are stripped before taking the first four — a name
 * like "125 Ocean Ave" reads as "OCEA", not "125O" or "OC A".
 */
export function projectCode(name) {
  const letters = String(name || '').replace(/[^A-Za-z]/g, '').toUpperCase()
  return letters.slice(0, 4) || null
}

/**
 * The on-screen flight label: project code + the flight's own type prefix, e.g. "DHSR-CFW".
 *
 * 2026-09-03 (Matt): "we don't need an actual digit number for milestones — just use the
 * milestone prefix after the project prefix, it will be all letters and that's fine." Squadron
 * (SQD###) and individual thread-crew (THR###) flights are NOT milestones — those keep their
 * digits, since unlike a milestone's own sequence number they carry no separate real-world
 * identity anywhere else the way a thread's own number does (see `threadTag` below). The letter
 * prefix is derived by stripping the flight's own trailing digits rather than reading a separate
 * `prefix` field — `flight_number` is always `${prefix}${seq}` with the prefix pure letters
 * (api/flights.js's `derivePrefix` and every curated "Major Drawing Release Abbreviation" are
 * letters-only), so this needs no API changes and works for every flight already minted.
 */
export function flightTag(f) {
  if (!f) return ''
  const code = projectCode(f.projectName)
  const isMilestone = !f.kind || f.kind === 'milestone'
  const label = isMilestone ? String(f.flightNumber || '').replace(/[0-9]+$/, '') || f.flightNumber || '' : (f.flightNumber || '')
  return code ? `${code}-${label}` : label
}

/**
 * The on-screen THREAD label: project code + a short thread-type code + the thread's own real
 * number, e.g. "FOUL-INS-101". Matt: "For threads, we do want the number included... FOUL-INS-
 * ###. I know we assign numbers to threads because I see them on the meeting minutes... I
 * remember setting it up in Airtable." Confirmed still true (2026-09-03): the Threads table's
 * "Thread Number" is a plain Airtable number field (not a native autonumber — something upstream
 * of this app sets it, since no app code writes it) and "Thread UID" — what actually prints on
 * meeting minutes — is a formula, `{Project Number}.{Thread Number}` (e.g. "24015.12"), no type
 * abbreviation in it at all. So THIS tag's type code (THREAD_TYPE_CODES below) is a Flight-Board-
 * only convention layered on top, purely for display — same footing as `projectCode` — while the
 * number itself is the real, shared "Thread Number" read live off Airtable
 * (api/thread-hill.js's `openThreadsById`/`threadHillPlan`). That number is Airtable-only, not
 * mirrored into Supabase, so it's only known while the live Airtable read succeeds — a stale or
 * landed/closed thread degrades to project+type with no trailing number rather than guessing.
 */
const THREAD_TYPE_CODES = {
  'Schedule and Progress': 'SCH',
  'Submittals': 'SUB',
  'RFIs': 'RFI',
  'PCOs and Change Orders': 'PCO',
  'Contractor Items': 'CON',
  'Inspector Items': 'INS',
  'Owner Items': 'OWN',
  'Architect Items': 'ARC',
  'Design Approvals': 'DAP',
  'Design Direction': 'DIR',
  'Specialty Design': 'SPD',
  'Permitting Items': 'PER',
  'Tendering Items': 'TEN',
  'Misc. Items': 'MIS',
  'Engineering Items': 'ENG',
}
export function threadTag(th) {
  if (!th) return ''
  const code = projectCode(th && th.projectName)
  const typeCode = th && THREAD_TYPE_CODES[th.type]
  const base = [code, typeCode].filter(Boolean).join('-') || (th && th.type) || 'Thread'
  return (th && th.threadNumber != null) ? `${base}-${th.threadNumber}` : base
}

// Click-vs-drag disambiguation for an on-curve flight/thread (2026-08-28) — Matt: "When I click
// on a plane or helicopter in the air, it immediately tries to change its location on the curve
// and asks for a reason to move it. I want instead for it to bring up the same menu we see when
// clicking the ellipses button in the same item's line item in the charts. I want it to be sure
// I am trying to drag to move it before it just asks me for the reason I am moving it."
//
// Root cause: the old onPointerDown called startDrag immediately, and the drag-vs-click test at
// drop time compared positions in T-SPACE (`> 0.005`) — on a wide chart that's only a few
// pixels of tolerance, well inside normal click jitter, so an ordinary click routinely read as
// an intentional drag. Fixed with a real pixel-space threshold (DRAG_PROMOTE_PX) measured from
// the pointerdown origin: below it, releasing the pointer opens the SAME ActionMenu
// FlightCard/ThreadCard's own ⋯ button opens (Open/Change crew/Mark landed/etc.); only crossing
// it promotes to a real drag, at which point these local listeners detach and hand off to
// `onStartDrag` (the existing global drag machinery) — no double-handling, since only one of
// the two ever ends up attached to `window` at a time.
const DRAG_PROMOTE_PX = 6

/**
 * One flight on the curve — icon, hover card, tag, and now (2026-08-28) the same action menu
 * its row in "In the air" already has, opened by a plain click. See DRAG_PROMOTE_PX's own
 * comment for why a click no longer risks being read as a drag.
 */
function OnCurveFlight({
  f, p, t, dragging, off, isHover, externalHover, busy, today, checklists, readOnly, inCluster, quiet, pinnedHere, onPin,
  onStartDrag, onHoverIn, onHoverOut, onOpenFlight, onCrew, onLand,
}) {
  const menu = useActionMenu()
  const pressRef = useRef(null)
  const iconRef = useRef(null)
  const stalled = f.stillDays >= STALL_DAYS
  // HOVER CARD WITH ACTIONS (2026-09-10, Matt: "when we hover, you see the actions in the popover
  // and we can click them there — that way we don't do two actions"). The card is portaled and
  // floats a finger's width below the icon, so a plain pointerleave would drop it before the mouse
  // could ever reach it. Leaving the icon OR the card starts a short grace timer; re-entering either
  // cancels it. The card itself reports enter/leave through the same two functions, so it stays up
  // exactly as long as the mouse is on the plane or on the card, and closes ~0.3s after both.
  const leaveTimer = useRef(null)
  const holdHover = () => { if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null } onHoverIn && onHoverIn() }
  const releaseHover = () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); leaveTimer.current = setTimeout(() => { leaveTimer.current = null; onHoverOut && onHoverOut() }, 280) }
  useEffect(() => () => { if (leaveTimer.current) clearTimeout(leaveTimer.current) }, [])
  // The five actions the click menu offers, for the card. Each one drops the hover first — an
  // overlay (modal, confirm) is about to sit on top, and the pointerleave that would normally
  // close the card can't be relied on once it does (same reasoning as the menu-open path below).
  const cardActions = readOnly ? null : {
    open:   () => { onHoverOut && onHoverOut(); onOpenFlight && onOpenFlight(f) },
    crew:   () => { onHoverOut && onHoverOut(); onCrew && onCrew(f) },
    land:   () => { onHoverOut && onHoverOut(); onLand && onLand(f, 'posted') },
    pull:   () => { onHoverOut && onHoverOut(); onLand && onLand(f, 'released') },
    cancel: () => { onHoverOut && onHoverOut(); onLand && onLand(f, 'cancelled') },
  }
  // THE REAL BUG (found 2026-08-28, after the effectiveHover fix below turned out not to be
  // enough — Matt kept reporting "I click these buttons and nothing happens" after that shipped):
  // the menu was anchored to `e.currentTarget` in handlePointerDown — the OUTER wrapping div,
  // which carries no width/height of its own (every child inside it is `position:absolute`, so
  // the wrapper's own getBoundingClientRect() is a literal 0×0 point at the icon's CENTER — verified
  // live). ActionMenu's reposition() computes `top = anchor.bottom + 4`, so with a 0×0 anchor that
  // put the menu's top a mere 4px below the icon's CENTER — while the icon itself (a real ~26-29px
  // element) extends another ~13-14px below that same center. The menu opened roughly 9px INSIDE
  // the icon's own footprint. "Open milestone," the item nearest the icon, sat in exactly that
  // overlap band: a real click aimed at it that lands a few px high hits the icon instead, which
  // just toggles its own already-open menu shut — no navigation, no visible error, "nothing
  // happens." Anchoring to `iconRef` (the real, sized icon div below) instead of the wrapper gives
  // the menu a genuine gap below the icon's true bottom edge.
  const effectiveHover = isHover || menu.isOpen || !!pinnedHere
  // "Grow and glow" when the matching row is hovered in the "In the air" list below the chart
  // (FlightPlanTab, 2026-09-03: "cause that icon in the curve to slightly grow and glow so I can
  // see where it is on the line at a glance"). Deliberately separate from `effectiveHover` —
  // that also opens the HoverCard and arms the click menu, which would be a lot of motion to
  // trigger just by mousing over a distant row; `glow` only nudges scale and adds a colored
  // drop-shadow, on the same icon element effectiveHover already scales.
  const glow = externalHover && !dragging
  // Kept from the earlier round: now that the anchor is a REAL element whose own size depends on
  // hover/scale (unlike the 0×0 wrapper above), it matters that the icon can't shrink out from
  // under its own open menu — see effectiveHover's use in the scale transform below.

  const handlePointerDown = (e) => {
    if (readOnly) return
    // THE ACTUAL BUG behind "clicking a menu item just closes the menu, nothing opens" (found
    // 2026-08-28 after two wrong fixes; keyboard Enter always worked, mouse/trackpad never did):
    // React portals propagate events through the REACT tree, not the DOM tree. The ActionMenu
    // below is portaled to document.body visually, but it is still a React CHILD of this
    // wrapper — so a pointerdown on "Open milestone" bubbles back up to THIS handler, which
    // armed the click-vs-drag logic, and its pointerup then called menu.toggle(), unmounting
    // the menu BEFORE the browser fired the click on the button. (Enter works because keyboard
    // activation emits no pointerdown; the earlier synchronous synthetic-event tests passed
    // because React batched the close until after the fake click.) Reproduced deterministically
    // with async-spaced events before fixing. The guard: a press that originates inside the
    // portaled menu is the menu's business, never the icon's.
    if (e.target && e.target.closest && e.target.closest('.wf-menu')) return
    // Same rule for the hover card's own buttons (2026-09-10): it is portaled too, so its presses
    // bubble back here through the React tree. A press inside the card is the card's business.
    if (e.target && e.target.closest && e.target.closest('.ft-hover-card')) return
    e.preventDefault()
    pressRef.current = { x: e.clientX, y: e.clientY, moved: false }
    const onMove = (ev) => {
      const press = pressRef.current
      if (!press || press.moved) return
      if (Math.hypot(ev.clientX - press.x, ev.clientY - press.y) > DRAG_PROMOTE_PX) {
        press.moved = true
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        onStartDrag('flight', f, ev)   // hands off to the parent's own drag effect from here
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (pressRef.current && !pressRef.current.moved) {
        // One popup at a time (2026-10-27): a click pins this plane's card with its actions, not a separate menu.
        if (onPin) onPin(f); else menu.toggle(iconRef.current)
        // Release the hover the moment a click opens the menu — the pointerleave this state
        // normally waits for can never fire reliably once an overlay (menu, then possibly the
        // peek modal's IFRAME, which swallows pointer events wholesale) sits on top, which left
        // the hover card stuck on screen after interacting (Matt's report). The card stays
        // visible while the menu is open regardless, via effectiveHover's menu.isOpen half.
        onHoverOut && onHoverOut()
      }
      pressRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <div
      style={{
        position: 'absolute', left: p.x, top: p.y,
        zIndex: dragging ? 30 : (effectiveHover || glow) ? 20 : (off ? off.z : 10),
        transition: dragging ? 'none' : 'left 420ms cubic-bezier(.22,.8,.3,1), top 420ms cubic-bezier(.22,.8,.3,1)',
        cursor: !readOnly && isMyPlane(f) ? 'grab' : 'pointer', userSelect: 'none', touchAction: 'none',
        opacity: busy === f.flightId ? 0.45 : (dragging || effectiveHover || glow ? 1 : (off ? off.opacity : 1)),
      }}
      onPointerDown={handlePointerDown}
      onPointerEnter={onHoverIn ? holdHover : undefined}
      onPointerLeave={onHoverIn ? releaseHover : undefined}
      // onDoubleClick removed 2026-08-28 (Matt's own hypothesis, being tested): a native dblclick
      // is timing-and-proximity based at the browser/OS level, and a fast click on the icon
      // followed shortly by a click on its own just-opened menu item is exactly the kind of
      // sequence that could interact with double-click detection unpredictably, especially on a
      // trackpad. "Open milestone" is already reachable from the menu, so this was a convenience
      // shortcut, not load-bearing — safe to drop while we isolate the bug.
      title={readOnly ? `${flightTag(f)}${f.note ? ' — ' + f.note : ''}` : undefined}
    >
      {/* On a past day, the planes that MOVED that day are the story — ring them. */}
      {readOnly && f.movedToday && <span style={S.movedRing} />}
      {/* IN THE WAY (2026-09-16): the captain has named something in the way two reports running, or
          says there is nothing left but waiting — red, and it outranks the amber stall ring. */}
      {!readOnly && (f.inWay || f.waitingSince) && !dragging && <span style={S.blockRing} />}
      {!readOnly && !(f.inWay || f.waitingSince) && stalled && !dragging && <span style={S.stallRing} />}
      {/* Info only: the card never takes the pointer (Matt, 2026-10-27: it blocked the planes behind it).
          The actions are on the plane's click menu. In a cluster, ClusterCard lists every plane instead. */}
      {isHover && !dragging && !inCluster && !quiet && (
        <HoverCard f={f} t={t} today={today} anchorEl={iconRef.current}
          cl={checklists && checklists[f.milestoneId]} />
      )}
      {dragging && t >= LANDING_T && (
        <span style={{ ...S.landPreview, position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -30px)' }}>
          Release to close this out
        </span>
      )}
      <div ref={iconRef} data-ft-icon={f.flightId} style={{
        position: 'absolute', left: 0, top: 0,
        transform: `translate(-50%,-50%) scale(${dragging ? 1.22 : effectiveHover ? 1.1 : glow ? 1.12 : (off ? off.scale : 1)})`,
        transition: dragging ? 'none' : 'transform 160ms ease, filter 160ms ease',
        filter: dragging ? 'drop-shadow(0 6px 10px rgba(7,39,45,0.28))' : glow ? GLOW_FILTER : undefined,
      }}>
        {/* Idle bob lives on its OWN inner element — see the keyframes' own comment — so it
            layers onto the outer div's translate/scale instead of fighting it for one
            transform string. Off while dragging: a plane under the cursor must track exactly. */}
        <span style={!dragging ? {
          display: 'block',
          animation: `ft-plane-bob 3.2s ease-in-out ${bobDelay(f.flightId, 3.2).toFixed(2)}s infinite`,
        } : undefined}>
          <Plane
            deg={attitudeFor(t)} spin={!dragging}
            color={readOnly
              ? (f.movedToday ? PALETTE.blue : 'var(--aj-text-muted)')
              : stalled ? PALETTE.amber
              : (f.latestFinish && f.latestFinish < today) ? PALETTE.red
              : (dragging && t >= LANDING_T) ? PALETTE.green
              : PALETTE.blue}
            size={26} />
        </span>
      </div>
      {/* Open-kit badge (Step 2, 2026-09-03, Matt: "some kind of indicator that the tower left
          items in the kit incomplete that need to be attended to — like the indicator for low
          fuel") — the LIVE count of this milestone's open Full-Kit checklist items, up and to the
          LEFT of the icon (fuel's "!" takes the right). Same still-sitting treatment as the fuel
          badge below, for the same reason. Clears itself as the items get ticked; the waiver
          reason, if any, is on the hover card. Threads/squadrons have no kit (f.kit is null). */}
      {!readOnly && !dragging && f.kit && f.kit.preworkOpen > 0 && (
        <span
          style={{
            position: 'absolute', left: 0, top: 0,
            transform: 'translate(-11px, -15px) translate(-50%,-50%)',
            minWidth: 13, height: 13, padding: '0 3px', boxSizing: 'border-box', borderRadius: 7,
            background: PALETTE.amber, color: '#07272D',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: '"DM Mono", monospace', fontWeight: 700, fontSize: 9, lineHeight: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.35)', border: '1px solid rgba(7,39,45,0.25)',
            pointerEvents: 'none',
          }}
          title={`Open kit — ${f.kit.preworkOpen} Full-Kit item${f.kit.preworkOpen === 1 ? '' : 's'} still open${f.kitWaiverNote ? ' · launched on a waiver: ' + f.kitWaiverNote : ''}`}
        >{f.kit.preworkOpen}</span>
      )}
      {/* Out-of-fuel warning badge (2026-09-01, Matt: "so we don't have to hover over to see
          it's in trouble") — a small amber "!" up and to the right of the icon. Deliberately a
          SEPARATE sibling of the icon div above, not nested inside its bob `<span>` or its own
          scale/rotate transform: it must sit still (no bob, no attitude rotation, no hover-scale)
          while the plane it's flagging keeps moving underneath it — only the OUTER wrapper's
          left/top glide carries it along the curve, same as the tag label below. Same red-tier
          threshold `fuelTone` already uses elsewhere (Over Budget / At Risk) — "out of fuel," not
          the milder Watch tier, which stays a hover-only detail. Threads/squadrons carry no
          phase-fuel signal at all (`f.fuel` is always null for them), so this never renders there.
          Reads `ebrStatus` (2026-09-03) — the EBR-based gauge, not the budget-remaining `status`
          still used to rank Next Up (see api/flights.js#computePhaseFuel's own doc comment). */}
      {!readOnly && !dragging && f.fuel && (f.fuel.ebrStatus === 'Over Budget' || f.fuel.ebrStatus === 'At Risk') && (
        <span
          style={{
            position: 'absolute', left: 0, top: 0,
            transform: 'translate(11px, -15px) translate(-50%,-50%)',
            width: 13, height: 13, borderRadius: 7,
            background: PALETTE.amber, color: '#07272D',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: '"DM Mono", monospace', fontWeight: 700, fontSize: 9, lineHeight: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.35)', border: '1px solid rgba(7,39,45,0.25)',
            pointerEvents: 'none',
          }}
          title={`Out of fuel — ${f.fuel.ebrFuelLabel}${f.phaseName ? ' · ' + f.phaseName : ''}`}
        >!</span>
      )}
      {/* A clustered flight's tag is suppressed at rest — Matt: "looks like dog shit" was five
          overlapping wide text labels each nudged 6px from the last, which reads as one
          smeared streak no matter how small the nudge. A LONE flight (off is null, the
          ordinary case) still always shows its tag exactly as before; only a member of an
          actual stack hides it until hovered or dragged, when there's only ever one label on
          screen at a time. */}
      {(!off || effectiveHover || dragging) && (
        <span style={{ ...S.tag, ...(effectiveHover || dragging ? S.tagOn : null), position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, 16px)' }}>
          {flightTag(f)}
        </span>
      )}
      {!readOnly && !onPin && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); onOpenFlight && onOpenFlight(f) }}>Open milestone</ActionMenuItem>
          <ActionMenuItem onClick={() => { menu.close(); onCrew && onCrew(f) }}>Change crew</ActionMenuItem>
          <ActionMenuDivider />
          <ActionMenuItem onClick={() => { menu.close(); onLand && onLand(f, 'posted') }}>Mark landed</ActionMenuItem>
          <ActionMenuItem onClick={() => { menu.close(); onLand && onLand(f, 'released') }}>Pull from the air</ActionMenuItem>
          <ActionMenuItem onClick={() => { menu.close(); onLand && onLand(f, 'cancelled') }} danger>Cancel flight</ActionMenuItem>
        </ActionMenu>
      )}
    </div>
  )
}

/** One thread on the curve — the same click-vs-drag/action-menu treatment as OnCurveFlight,
 * minus the parts that don't apply to a Thread (no crew, no checklist, no readOnly gate since
 * threads don't render in readOnly/history-scrub mode at all — see the caller). */
function OnCurveThread({
  th, p, t, dragging, off, isHover, externalHover, today,
  onStartDrag, onHoverIn, onHoverOut, onOpenThread, onPull, onFastTrack, onCrew,
}) {
  const menu = useActionMenu()
  const pressRef = useRef(null)
  const iconRef = useRef(null)
  const stalled = th.stillDays >= STALL_DAYS
  const late = th.targetDeadline && th.targetDeadline < today
  // See the real-bug comment in OnCurveFlight: the menu was anchored to the wrapping div, a
  // literal 0×0 point at the icon's CENTER (every child is absolutely positioned, so the wrapper
  // itself has no size) — putting the menu ~9px inside the icon's own footprint instead of below
  // it. Anchor to iconRef (the real, sized icon div) instead, so the menu gets a genuine gap.
  const effectiveHover = isHover || menu.isOpen
  // See OnCurveFlight's own comment — same "In the air" row-hover glow, deliberately not folded
  // into effectiveHover (that also opens the HoverCard/menu).
  const glow = externalHover && !dragging

  const handlePointerDown = (e) => {
    // Portal-bubble guard — see the full explanation in OnCurveFlight's handlePointerDown: a
    // press on the portaled menu's own items bubbles back here through the React tree, and
    // without this the pointerup below toggles the menu closed before the item's click fires.
    if (e.target && e.target.closest && e.target.closest('.wf-menu')) return
    e.preventDefault()
    pressRef.current = { x: e.clientX, y: e.clientY, moved: false }
    const onMove = (ev) => {
      const press = pressRef.current
      if (!press || press.moved) return
      if (Math.hypot(ev.clientX - press.x, ev.clientY - press.y) > DRAG_PROMOTE_PX) {
        press.moved = true
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        onStartDrag('thread', th, ev)
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (pressRef.current && !pressRef.current.moved) {
        menu.toggle(iconRef.current)
        onHoverOut && onHoverOut()   // release the hover on click — see OnCurveFlight's comment
      }
      pressRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <div
      style={{
        position: 'absolute', left: p.x, top: p.y,
        zIndex: dragging ? 30 : (effectiveHover || glow) ? 20 : (off ? off.z : 10),
        transition: dragging ? 'none' : 'left 420ms cubic-bezier(.22,.8,.3,1), top 420ms cubic-bezier(.22,.8,.3,1)',
        cursor: 'grab', userSelect: 'none', touchAction: 'none',
        opacity: dragging || effectiveHover || glow ? 1 : (off ? off.opacity : 1),
      }}
      onPointerDown={handlePointerDown}
      onPointerEnter={onHoverIn}
      onPointerLeave={onHoverOut}
      // onDoubleClick removed 2026-08-28 — see the matching comment in OnCurveFlight.
      title={`${th.title || 'Thread'} — click for actions, drag to move along the flight path`}
    >
      {stalled && !dragging && <span style={S.stallRing} />}
      {effectiveHover && !dragging && (
        <ThreadHoverCard t={th} pos={t} today={today} anchorEl={iconRef.current} />
      )}
      {dragging && t >= LANDING_T && (
        <span style={{ ...S.landPreview, position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -28px)' }}>
          Release to close this thread
        </span>
      )}
      <div ref={iconRef} style={{
        position: 'absolute', left: 0, top: 0,
        transform: `translate(-50%,-50%) scale(${dragging ? 1.22 : effectiveHover ? 1.1 : glow ? 1.12 : (off ? off.scale : 1)})`,
        transition: dragging ? 'none' : 'transform 160ms ease, filter 160ms ease',
        filter: dragging ? 'drop-shadow(0 6px 10px rgba(7,39,45,0.28))' : glow ? GLOW_FILTER : undefined,
      }}>
        {/* Faster, asymmetric wobble vs. the plane's slow glide — see ft-heli-bob's own comment.
            Off while dragging, same reasoning as OnCurveFlight. */}
        <span style={!dragging ? {
          display: 'block',
          animation: `ft-heli-bob 1.7s ease-in-out ${bobDelay(th.threadId, 1.7).toFixed(2)}s infinite`,
        } : undefined}>
          <Plane
            deg={threadAttitudeFor(t)} spin={!dragging} icon="heli"
            color={(dragging && t >= LANDING_T) ? PALETTE.green : stalled ? PALETTE.amber : late ? PALETTE.red : PALETTE.teal}
            size={22} />
        </span>
      </div>
      {/* Same suppression as the flights above, same reason: a cluster of threads each showing
          their own wide "code · type" tag at a 6px stagger is what produced the red streak —
          one label at a time, not five stacked on top of each other. */}
      {(!off || effectiveHover || dragging) && (
        <span style={{ ...S.tag, ...(effectiveHover || dragging ? S.tagOn : null), position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, 14px)' }}>
          {threadTag(th)}
        </span>
      )}
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onOpenThread && onOpenThread(th) }}>Open thread</ActionMenuItem>
        {onCrew && (
          <ActionMenuItem onClick={() => { menu.close(); onCrew(th) }}>
            {(th.crew || []).length ? 'Change crew' : 'Crew this thread…'}
          </ActionMenuItem>
        )}
        {onFastTrack && (
          <ActionMenuItem onClick={() => { menu.close(); onFastTrack(th) }}
            title="Close administratively: complete open subtasks + record a Fast-tracked closure">
            Fast track to landing
          </ActionMenuItem>
        )}
        <ActionMenuItem onClick={() => { menu.close(); onPull && onPull(th) }} danger>Pull from the curve</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/**
 * A Squadron on the curve — up to 5 threads bundled into one crewed unit
 * (database/202_squadrons.sql). At rest it's a single cluster icon with a count badge; hovering
 * fans the member threads out into individually-hoverable mini icons in an arc above the
 * cluster, per Matt's spec: "show clustered together on the curve and when hovered over should
 * fan out so we can then hover our mouse over an individual one to see what it is."
 *
 * No drag (see the render site's own comment) — click opens the same action-menu treatment as
 * a flight/thread, just with squadron-shaped actions: crew the whole unit, drop one member back
 * to flying solo, or disband entirely.
 */
function OnCurveSquadron({
  f, p, t, dragging, off, isHover, externalHover, today, onStartDrag, onHoverIn, onHoverOut,
  onCrew, onLand, onRemoveThread, onAddThread, onOpenThread,
}) {
  const menu = useActionMenu()
  const pressRef = useRef(null)
  const iconRef = useRef(null)
  const members = f.squadronThreads || []
  const effectiveHover = isHover || menu.isOpen
  // See OnCurveFlight's own comment — same "In the air" row-hover glow.
  const glow = externalHover && !dragging
  const n = members.length

  // Fan the members out along a small arc above the cluster — evenly spaced, widest first item
  // to the left. A single-member squadron (shouldn't really happen, but not invalid) just sits
  // straight up.
  const fanAngle = 130   // degrees of arc the fan sweeps
  const fanRadius = 30
  const fanPositions = members.map((m, i) => {
    const frac = n > 1 ? i / (n - 1) : 0.5
    const deg = 270 - fanAngle / 2 + frac * fanAngle   // centered on straight-up (270deg in screen coords)
    const rad = (deg * Math.PI) / 180
    return { dx: Math.cos(rad) * fanRadius, dy: Math.sin(rad) * fanRadius, m }
  })

  // Same click-vs-drag disambiguation as OnCurveFlight/OnCurveThread (2026-09-01 — squadrons
  // could not be dragged at all before this; Matt reported it, and separately reported two
  // squadrons landing in the exact same spot with no way to tell them apart — the `off` prop's
  // stacking, computed by the caller exactly like it already is for threads, is the other half
  // of that fix).
  const handlePointerDown = (e) => {
    if (e.target && e.target.closest && e.target.closest('.wf-menu')) return
    if (e.target && e.target.closest && e.target.closest('[data-squadron-member]')) return
    e.preventDefault()
    pressRef.current = { x: e.clientX, y: e.clientY, moved: false }
    const onMove = (ev) => {
      const press = pressRef.current
      if (!press || press.moved) return
      if (Math.hypot(ev.clientX - press.x, ev.clientY - press.y) > DRAG_PROMOTE_PX) {
        press.moved = true
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        onStartDrag('squadron', f, ev)
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (pressRef.current && !pressRef.current.moved) {
        menu.toggle(iconRef.current)
        onHoverOut && onHoverOut()
      }
      pressRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <div
      style={{
        position: 'absolute', left: p.x, top: p.y,
        zIndex: dragging ? 30 : (effectiveHover || glow) ? 25 : (off ? off.z : 12),
        transition: dragging ? 'none' : 'left 420ms cubic-bezier(.22,.8,.3,1), top 420ms cubic-bezier(.22,.8,.3,1)',
        cursor: 'grab', userSelect: 'none', touchAction: 'none',
        opacity: dragging || effectiveHover || glow ? 1 : (off ? off.opacity : 1),
      }}
      onPointerDown={handlePointerDown}
      onPointerEnter={onHoverIn}
      onPointerLeave={onHoverOut}
      title={dragging ? undefined : `Squadron ${f.flightNumber || ''} — ${n} thread${n === 1 ? '' : 's'} crewed together — click for actions, drag to move, hover to see which`}
    >
      {dragging && t >= LANDING_T && (
        <span style={{ ...S.landPreview, position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -30px)' }}>
          Squadrons don't close by dragging — disband from its own menu
        </span>
      )}
      {/* Fanned-out members — only rendered on hover, each independently hoverable/clickable. */}
      {effectiveHover && !dragging && fanPositions.map(({ dx, dy, m }) => (
        <div
          key={m.threadId}
          data-squadron-member="1"
          onClick={(e) => { e.stopPropagation(); onOpenThread && onOpenThread(m) }}
          title={`${m.title || 'Thread'}${m.type ? ' · ' + m.type : ''}`}
          style={{
            position: 'absolute', left: dx, top: dy, transform: 'translate(-50%,-50%)',
            cursor: 'pointer',
          }}
        >
          <Plane deg={0} spin={false} icon="heli" color={PALETTE.purple} size={16} />
        </div>
      ))}
      {effectiveHover && !dragging && (
        <SquadronHoverCard f={f} t={t} today={today} anchorEl={iconRef.current} />
      )}
      <div ref={iconRef} style={{
        position: 'absolute', left: 0, top: 0,
        transform: `translate(-50%,-50%) scale(${dragging ? 1.22 : effectiveHover ? 1.15 : glow ? 1.17 : (off ? off.scale : 1)})`,
        transition: dragging ? 'none' : 'transform 160ms ease, filter 160ms ease',
        filter: dragging ? 'drop-shadow(0 6px 10px rgba(7,39,45,0.28))' : glow ? GLOW_FILTER : undefined,
      }}>
        <span style={!dragging ? {
          display: 'block',
          animation: `ft-heli-bob 1.7s ease-in-out ${bobDelay(f.flightId, 1.7).toFixed(2)}s infinite`,
        } : undefined}>
          <Plane deg={0} spin={false} icon="heli" color={PALETTE.purple} size={24} />
        </span>
        <span style={{
          position: 'absolute', right: -6, top: -6,
          minWidth: 15, height: 15, padding: '0 3px', borderRadius: 8,
          background: PALETTE.purple, color: '#fff', fontFamily: '"DM Mono", monospace', fontSize: 9,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }}>{n}</span>
      </div>
      {(!off || effectiveHover || dragging) && (
        <span style={{ ...S.tag, ...(effectiveHover || dragging ? S.tagOn : null), position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, 16px)' }}>
          Squadron · {n} thread{n === 1 ? '' : 's'}
        </span>
      )}
      {!dragging && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); onCrew && onCrew(f) }}>Crew squadron</ActionMenuItem>
          {n < 5 && (
            <ActionMenuItem onClick={() => { menu.close(); onAddThread && onAddThread(f) }}>Add threads…</ActionMenuItem>
          )}
          {members.length > 0 && <ActionMenuDivider />}
          {members.map(m => (
            <ActionMenuItem key={m.threadId}
              onClick={() => { menu.close(); onRemoveThread && onRemoveThread(f, m) }}>
              Release {m.title || 'thread'} back to flying solo
            </ActionMenuItem>
          ))}
          <ActionMenuDivider />
          <ActionMenuItem onClick={() => { menu.close(); onLand && onLand(f, 'released') }} danger>Disband squadron</ActionMenuItem>
        </ActionMenu>
      )}
    </div>
  )
}

/** What a Squadron is, on hover — the same idea as HoverCard/ThreadHoverCard, so all three read
 * as one family. Matt, 2026-09-01: "I want the same tooltip type popup that we see on planes and
 * helos to also show for squadrons so we can see what project they are for and who's currently
 * crewing." */
function SquadronHoverCard({ f, t, today, anchorEl }) {
  const members = f.squadronThreads || []
  const stalled = f.stillDays >= STALL_DAYS
  return (
    <FloatingCard anchorEl={anchorEl} style={S.hover}>
      <div style={S.hoverTop}>
        <span style={S.hoverNo}>{f.flightNumber}</span>
        <span style={{ ...S.hoverPos, color: stalled ? PALETTE.amber : 'var(--aj-text-mid)' }}>
          {hillRead(t)} · {Math.round(t * 100)}%
        </span>
      </div>
      <div style={S.hoverTitle}>{f.projectName || 'Project not named'}</div>
      <div style={S.hoverMs}>{members.length} thread{members.length === 1 ? '' : 's'} bundled</div>
      <div style={S.hoverRows}>
        <Row k="Crew" v={f.crew && f.crew.length ? f.crew.map(c => c.name + (c.priority === 1 ? ' 1st' : c.priority === 2 ? ' 2nd' : '') + (c.isCaptain ? ' (capt)' : '')).join(', ') : 'none assigned'}
          tone={f.crew && f.crew.length ? null : PALETTE.amber} />
        <Row k="Members" v={members.map(m => m.title || 'Untitled thread').join(', ') || '—'} />
        <Row k="Last moved" v={f.stillDays === 0 ? 'today' : `${f.stillDays}d ago`}
          tone={stalled ? PALETTE.amber : null} />
      </div>
      {stalled && <div style={S.hoverWarn}>No one has moved this plane along the path in {f.stillDays} days. Its captain moves it in their daily report.</div>}
    </FloatingCard>
  )
}

/** Plain-English reading of a hill position. Mirrors hillLabel in FlightPlanTab. */
function hillRead(t) {
  if (t < 0.18) return 'Wheels up'
  if (t < 0.42) return 'Plotting the course'
  if (t < 0.58) return 'Course set'
  if (t < 0.85) return 'Flying the course'
  return 'On final approach'
}

/**
 * What a plane is, on hover.
 *
 * A bare flight number told you nothing you couldn't already see. This is the whole situation:
 * where it is on the hill and what that means, who is flying it, when it is due and how late,
 * how long since anyone committed a view, and — deliberately alongside the judged position —
 * the checklist count, so the generated number and the judged one can be read against each
 * other in one glance.
 *
 * Positioned by FloatingCard, off the icon's own real screen coordinates — see its doc comment
 * for why chart-relative positioning couldn't reliably keep this on screen.
 */
function HoverCard({ f, t, today, anchorEl, cl, actions, onHold, onRelease, onBack, backCount }) {
  const late = f.latestFinish && f.latestFinish < today
  const lateBy = late
    ? Math.round((new Date(today + 'T00:00:00') - new Date(f.latestFinish + 'T00:00:00')) / 86400000)
    : 0
  const stalled = f.stillDays >= STALL_DAYS
  // With actions the card is a real target for the mouse (pointerEvents on), and it tells the
  // owning icon when the mouse is on it so the grace-period hover holds. Without actions (read-only
  // history days) it stays the old see-through card.
  const interactive = !!actions
  const stop = (e) => { e.stopPropagation() }
  return (
    <FloatingCard anchorEl={anchorEl} style={{ ...S.hover, pointerEvents: interactive ? 'auto' : 'none' }}
      className={interactive ? 'ft-hover-card' : undefined}
      onPointerEnter={interactive ? onHold : undefined} onPointerLeave={interactive ? onRelease : undefined}>
      {onBack && (
        <button type="button" onPointerDown={stop} onClick={(e) => { stop(e); onBack() }}
          style={{ font: 'inherit', fontSize: 11.5, color: 'var(--aj-text-mid)', background: 'none', border: 'none', padding: '0 0 6px', cursor: 'pointer' }}>
          ← All {backCount} planes here
        </button>
      )}
      <div style={S.hoverTop}>
        <span style={S.hoverNo}>{flightTag(f)}</span>
        <span style={{ ...S.hoverPos, color: stalled ? PALETTE.amber : 'var(--aj-text-mid)' }}>
          {hillRead(t)} · {Math.round(t * 100)}%
        </span>
      </div>
      <div style={S.hoverTitle}>{f.projectName || 'Project not named'}</div>
      <div style={S.hoverMs}>{f.name}</div>
      <div style={S.hoverRows}>
        <Row k="Crew" v={f.crew && f.crew.length ? f.crew.map(c => c.name + (c.priority === 1 ? ' 1st' : c.priority === 2 ? ' 2nd' : '') + (c.isCaptain ? ' (capt)' : '')).join(', ') : 'none assigned'}
          tone={f.crew && f.crew.length ? null : PALETTE.amber} />
        {f.phaseName && <Row k="Phase" v={f.phaseName} />}
        {/* Crawled from the same scheduling mirror the Gantt itself reads — the earliest-start
            gives a real window, not just a single deadline, and "planned arrival" is Matt's own
            wording for the finish side of it. */}
        <Row k="Planned arrival" v={f.latestFinish ? fmtDay(f.latestFinish) + (late ? ` · ${lateBy}d late` : '') : 'undated'}
          tone={late ? PALETTE.red : null} />
        {f.earliestStart && <Row k="Scheduled start" v={fmtDay(f.earliestStart)} />}
        {/* Reads the SAME live-fetched map the flight card's checklist body reads — never the
            Supabase mirror's completed/incomplete_checklist_items, which lags and once showed
            "0 of 0" here while the card below it correctly read 1/74. */}
        {cl
          ? <Row k="Checklist" v={`${cl.done} of ${cl.total} ticked`} />
          : <Row k="Checklist" v="loading…" tone="var(--aj-text-muted)" />}
        {/* Fuel is the PHASE's budget health, not this milestone's own — "a plane refuels at
            the end of each phase, not the end of each milestone" (Matt). Every flight sharing
            a phase shares this reading. Absent (null) when Phases/time_entries couldn't be
            read this load — shown as loading, not silently skipped, so it's never mistaken
            for "nothing to report." EBR-based (2026-09-03) — reads `ebrFuelLabel`/`ebrStatus`,
            not the budget-remaining `fuelLabel`/`status` that still ranks Next Up (see
            api/flights.js#computePhaseFuel). Shows the actual rate against target so the reading
            is arguable, not just a color — Matt: "look how we do the indicators in our time &
            expense reviewer" (phaseLandscape.js shows the same "$X of target" alongside its dot). */}
        {f.fuel
          ? <Row k="Fuel" v={f.fuel.ebrFuelLabel
              + (f.fuel.ebr != null ? ` · $${Math.round(f.fuel.ebr)}/hr (target $${Math.round(f.fuel.targetEbr)})` : '')
              + (f.phaseName ? ` · ${f.phaseName}` : '')} tone={fuelTone(f.fuel.ebrStatus)} />
          : <Row k="Fuel" v="unavailable" tone="var(--aj-text-muted)" />}
        {/* Full kit (Step 2): the live Full-Kit count, and the tower's reason if it launched
            without it. Both read off the same authoritative crawl as the posted check. */}
        {f.passengers && f.passengers.length > 0 && <Row k="Passengers" v={f.passengers.map(p => p.title).join(' · ')} tone={PALETTE.purple} />}
        {f.kit && (f.kit.preworkOpen > 0
          ? <Row k="Kit" v={`${f.kit.preworkOpen} Full-Kit item${f.kit.preworkOpen === 1 ? '' : 's'} still open`} tone={PALETTE.amber} />
          : <Row k="Kit" v="complete" />)}
        {f.kitWaiverNote && <Row k="Launched on a waiver" v={f.kitWaiverNote} tone={PALETTE.amber} />}
        {/* The captain's daily report (docs/CAPTAIN_REPORT.md): days of their own work left, what is in
            the way and for how many reports running, and the tower's answer to a direction ask. */}
        {f.holdingSince
          ? <Row k="Holding" v={`${(f.holdingReason || 'waiting on someone outside').replace(/^Waiting/, 'waiting')} · since ${fmtDay(f.holdingSince)}${f.backPendingAt ? ' · back, no free seat' : ' · its seats are free for other work'}`} />
          : f.waitingSince
          ? <Row k="Captain says" v={`nothing left but waiting · since ${fmtDay(f.waitingSince)}`} tone={PALETTE.red} />
          : (f.daysRemaining != null && <Row k="Captain says" v={`${f.daysRemaining} day${Number(f.daysRemaining) === 1 ? '' : 's'} of own work left · ${fmtDay(f.daysRemainingAt)}`} />)}
        {f.inWay && <Row k="In the way" v={`${f.inWay.blockerLabel} · ${f.inWay.days} reports running`} tone={PALETTE.red} />}
        {!f.inWay && f.lastReport && f.lastReport.blocker !== 'none' && <Row k="In the way" v={f.lastReport.blockerLabel} tone={PALETTE.amber} />}
        {f.lastReport && f.lastReport.ack && <Row k="Tower answered" v={f.lastReport.ack.kind === 'handled' ? `handled${f.lastReport.ack.by ? ' by ' + f.lastReport.ack.by : ''}` : `${f.lastReport.ack.by || 'the tower'} will do it ${fmtDay(f.lastReport.ack.date)}`} />}
        <Row k="Last moved" v={f.stillDays === 0 ? 'today' : `${f.stillDays}d ago`}
          tone={stalled ? PALETTE.amber : null} />
      </div>
      {(f.inWay || f.waitingSince) && f.lastReport && f.lastReport.note && <div style={{ ...S.hoverWarn, color: PALETTE.red }}>{f.lastReport.by ? f.lastReport.by + ': ' : ''}“{f.lastReport.note}”</div>}
      {stalled && !(f.inWay || f.waitingSince) && <div style={S.hoverWarn}>No one has moved this plane along the path in {f.stillDays} days. Its captain moves it in their daily report.</div>}
      {/* The same five actions the click menu offers, right here on the card (2026-09-10). Each
          button swallows its own pointerdown so the icon's press/drag logic never sees it. */}
      {interactive && (
        <div style={S.hoverActions} onPointerDown={stop}>
          <button type="button" style={S.hoverBtn} onPointerDown={stop} onClick={(e) => { stop(e); actions.open() }}>Open milestone</button>
          <button type="button" style={S.hoverBtn} onPointerDown={stop} onClick={(e) => { stop(e); actions.crew() }}>Change crew</button>
          <button type="button" style={S.hoverBtn} onPointerDown={stop} onClick={(e) => { stop(e); actions.land() }}>Mark landed</button>
          <button type="button" style={S.hoverBtn} onPointerDown={stop} onClick={(e) => { stop(e); actions.pull() }}>Pull from the air</button>
          <button type="button" style={{ ...S.hoverBtn, ...S.hoverBtnDanger }} onPointerDown={stop} onClick={(e) => { stop(e); actions.cancel() }}>Cancel flight</button>
        </div>
      )}
    </FloatingCard>
  )
}
/** Fuel status → row color. Same bands as phaseFinancials.js#computeLiveFinancials. */
export function fuelTone(status) {
  if (status === 'Over Budget' || status === 'At Risk') return PALETTE.red
  if (status === 'Watch') return PALETTE.amber
  if (status === 'On Track') return PALETTE.green
  return 'var(--aj-text-muted)'   // 'No Budget Set'
}

const Row = ({ k, v, tone }) => (
  <div style={S.hoverRow}>
    <span style={S.hoverK}>{k}</span>
    <span style={{ ...S.hoverV, color: tone || 'var(--aj-text)' }}>{v}</span>
  </div>
)

/**
 * "+N" — sits at the tail of a stack's fan, one step past the last individually-rendered plane,
 * for whatever a cluster had left over past VISIBLE_STACK_CAP. Never silently dropped (see
 * declutterOffsets' own doc comment) — this is what says so.
 */
function OverflowBadge({ p, count, color }) {
  return (
    <div
      style={{
        position: 'absolute', left: p.x + VISIBLE_STACK_CAP * 6, top: p.y + VISIBLE_STACK_CAP * 6,
        transform: 'translate(-50%,-50%)', zIndex: 15,
        minWidth: 20, height: 20, padding: '0 5px', borderRadius: 10,
        background: color || '#07272D', color: '#fff', fontFamily: '"DM Mono", monospace', fontSize: 9.5,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'default',
      }}
      title={`${count} more clustered here — not shown individually`}
    >+{count}</div>
  )
}

/**
 * FloatingCard — portals a hover card to `document.body` and positions it with `position: fixed`
 * off the anchor element's REAL `getBoundingClientRect()`, clamped to the viewport on all four
 * edges. This REPLACES two prior rounds of chart-relative position guessing, both of which kept
 * recurring (Matt, 2026-09-01, three separate reports: SQD102, then WRO-PDR101, then "still
 * falling out of view... we've been chasing our tail"):
 *
 *   Round 1 fixed the VERTICAL flip by measuring the card's real height instead of guessing a
 *   fraction of curve height (three different hand-tuned constants, one per card type, each
 *   wrong for that card's own row count) — see git history for `useHoverFlip`. That was real
 *   progress but incomplete: it only ever chose "above" or "below" within chart-local
 *   coordinates, which are themselves bounded by two things no amount of smarter guessing fixes:
 *
 *   1. `S.wrap` (this component's own outer card) sets `overflow: hidden` for its rounded
 *      corners — a hover card positioned absolutely INSIDE that box can only ever be visible
 *      within it, no matter how correct its math is relative to the curve.
 *   2. Each on-curve icon's own wrapper carries its own `zIndex` (10/20/30 depending on
 *      drag/hover state) and is itself `position: absolute` — CSS gives that wrapper its own
 *      stacking context, so a hover card nested inside it with a locally-declared `zIndex: 60`
 *      only ever wins against ITS OWN siblings, never against a completely different part of the
 *      tree (the landed-deck stack, another icon's wrapper) declared with a higher z-index at
 *      the SAME level — which is why a card near the landing zone could render UNDER other
 *      icons there ("show under the planes and helos at the end").
 *
 * Escaping to a body-level portal with `position: fixed` sidesteps both in one move, the same
 * way `ActionMenu.jsx` already does for the action menu (mirrors its `reposition()` almost
 * exactly) — there is no ancestor left to clip it and no stacking context left to be capped by.
 * `anchorEl` is a real DOM node (the icon's own `iconRef.current`, already tracked by every
 * OnCurve* component for its action menu), not curve coordinates, so this needs no chart-shape
 * knowledge at all — no more `hAnchorFor`/`HOVER_W`-threshold left/right guessing, no more
 * `PAD.t`/curve-relative up/down guessing. One shared component, one shared bug surface.
 */
// THE 2026-09-02 REGRESSION, and the one-line reason the portal fix shipped "not showing at
// all": the first version used zIndex 90. The Flight Plan lives inside the Pulse workspace
// shell, `.aj-team-ws` in outline.css — `position: fixed; z-index: 400; background:
// var(--aj-bg-page)`, an OPAQUE layer covering the whole viewport. A body-level card at z 90
// paints UNDER that entire panel: present in the DOM, laid out, `visibility: visible`,
// getBoundingClientRect() exactly right — and completely hidden. That is precisely why every
// DOM-inspection check passed while every screenshot showed nothing (the screenshots were
// telling the truth; they were dismissed as a tool-scaling artifact — don't repeat that: when a
// screenshot and a DOM probe disagree about visibility, the screenshot wins). ActionMenu, the
// pattern this copies, never hit it because it sits at 99999. This sits ABOVE the workspace
// (400) and the app's modal tiers (700–9000; moot anyway, since `overlayOpen` clears hovers
// when a modal opens) but BELOW ActionMenu's 99999, so when a click opens the menu while the
// card is up — the intended touch behavior — the menu is the thing on top.
const FLOATING_Z = 9500

const NEAR_PX = 46      // a plane owns the pointer within this distance
const CLUSTER_PX = 24   // planes this close to the nearest one share one card

/**
 * One card for a bunch of planes (Matt, 2026-10-27). Lists each plane in flight-path order, the one nearest
 * the pointer highlighted; move the pointer across the bunch to pick one, click to open its menu. Sits beside
 * the bunch, never on it, and never takes the pointer.
 */
function ClusterCard({ cluster, chartEl, hoverId, flights, today, pinned, onSelect }) {
  if (!chartEl || !flights.length) return null
  const r = chartEl.getBoundingClientRect()
  const W = 300
  const right = r.left + cluster.x + 30 + W < window.innerWidth - 8
  const left = right ? r.left + cluster.x + 30 : r.left + cluster.x - 30 - W
  const top = Math.max(8, Math.min(window.innerHeight - 40 - flights.length * 56, r.top + cluster.y - 24))
  const rows = flights.slice().sort((a, b) => (b.hillPosition || 0) - (a.hillPosition || 0))
  return createPortal(
    <div className={pinned ? 'ft-hover-card' : undefined} style={{ ...S.hover, width: W, position: 'fixed', left, top, zIndex: FLOATING_Z, pointerEvents: pinned ? 'auto' : 'none' }}>
      <div style={{ ...S.hoverTop, marginBottom: 6 }}>
        <span style={S.hoverNo}>{rows.length} planes here</span>
        <span style={{ ...S.hoverPos, color: 'var(--aj-text-muted)' }}>{pinned ? 'pick one for its details and actions' : 'click to choose one'}</span>
      </div>
      {rows.map(f => {
        const on = f.flightId === hoverId
        const late = f.latestFinish && f.latestFinish < today
        const capt = (f.crew || []).find(c => c.isCaptain)
        const said = f.waitingSince ? 'only waiting now' : f.inWay ? `${f.inWay.blockerLabel}, ${f.inWay.days} reports` : f.daysRemaining != null ? `${f.daysRemaining}d of work left` : null
        return (
          <div key={f.flightId} role={pinned ? 'button' : undefined} tabIndex={pinned ? 0 : undefined}
            onClick={pinned ? () => onSelect(f.flightId) : undefined} onKeyDown={pinned ? (e) => { if (e.key === 'Enter') onSelect(f.flightId) } : undefined}
            className={pinned ? 'ft-cluster-row' : undefined}
            style={{ padding: '6px 8px', margin: '0 -6px', borderRadius: 6, cursor: pinned ? 'pointer' : undefined, background: on && !pinned ? 'var(--aj-bg-subtle)' : 'transparent', borderLeft: `2px solid ${on && !pinned ? PALETTE.blue : 'transparent'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
              <span style={{ fontWeight: 600, color: 'var(--aj-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{flightTag(f)} · {f.projectName || 'Project'}</span>
              <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', flex: '0 0 auto' }}>{Math.round((f.hillPosition || 0) * 100)}%</span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--aj-text-mid)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
            <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 1 }}>
              {capt ? capt.name.split(' ')[0] : 'no captain'}
              {f.latestFinish && <span style={{ color: late ? PALETTE.red : undefined }}> · arrives {fmtDay(f.latestFinish)}</span>}
              {said && <span style={{ color: f.inWay || f.waitingSince ? PALETTE.red : undefined }}> · {said}</span>}
            </div>
          </div>
        )
      })}
    </div>,
    document.body)
}

function FloatingCard({ anchorEl, style: ownStyle, children, className, onPointerEnter, onPointerLeave }) {
  const cardRef = useRef(null)
  // Visible from the very first paint rather than hidden-until-measured: the anchor's rect is
  // already known at mount (iconRef.current is an existing, already-laid-out node), so a
  // same-frame best guess (the caller's declared CSS width stands in for the not-yet-measured
  // real size) shows immediately and `reposition()` corrects it before paint via
  // useLayoutEffect. A modest robustness property — a card can never be stuck in a hidden
  // state — NOT the fix for the 2026-09-02 "no popup at all" regression; see FLOATING_Z below
  // for what that actually was. (Recorded honestly because a previous commit claimed otherwise.)
  const [pos, setPos] = useState(() => {
    if (!anchorEl) return { visibility: 'visible', top: 0, left: 0 }
    const a = anchorEl.getBoundingClientRect()
    const guessW = (typeof ownStyle?.width === 'number') ? ownStyle.width : 268
    let left = a.left + a.width / 2 - guessW / 2
    if (left + guessW > window.innerWidth - 8) left = window.innerWidth - guessW - 8
    if (left < 8) left = 8
    return { visibility: 'visible', top: a.bottom + 10, left }
  })

  const reposition = useCallback(() => {
    if (!anchorEl || !cardRef.current) return
    const a = anchorEl.getBoundingClientRect()
    const cw = cardRef.current.offsetWidth
    const ch = cardRef.current.offsetHeight
    const GAP = 10
    let left = a.left + a.width / 2 - cw / 2
    let top = a.bottom + GAP
    if (top + ch > window.innerHeight - 8) top = a.top - ch - GAP   // flip above if below would overflow
    if (top < 8) top = 8                                             // clamp — "above" can itself overflow near the very top
    if (left + cw > window.innerWidth - 8) left = window.innerWidth - cw - 8
    if (left < 8) left = 8
    setPos({ visibility: 'visible', top, left })
  }, [anchorEl])

  // Measure before paint (no visible jump), and again whenever the card's own rendered size
  // changes after that — a ResizeObserver catches every cause (checklist resolving from
  // "loading…" to a real count, crew names wrapping to a second line, …) without needing to
  // enumerate them as effect dependencies.
  useLayoutEffect(() => { reposition() }, [anchorEl, reposition])
  useEffect(() => {
    if (!cardRef.current) return
    const ro = new ResizeObserver(reposition)
    ro.observe(cardRef.current)
    return () => ro.disconnect()
  }, [reposition])
  // Same reasoning as ActionMenu's own listener: a scrollable ancestor (a full-bleed workspace's
  // inner content container) can move the anchor without ever firing a window-level scroll event.
  useEffect(() => {
    if (!anchorEl) return
    window.addEventListener('scroll', reposition, { capture: true, passive: true })
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, { capture: true })
      window.removeEventListener('resize', reposition)
    }
  }, [anchorEl, reposition])

  if (!anchorEl) return null
  return createPortal(
    <div ref={cardRef} className={className} style={{ ...ownStyle, position: 'fixed', zIndex: FLOATING_Z, ...pos }}
      onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
      {children}
    </div>,
    document.body
  )
}

/**
 * What a thread is, on hover — the same idea as HoverCard, minus the parts that don't apply to
 * a Thread (no crew, no checklist): where it sits on its own hill, what it is, and how long
 * since anyone committed a view.
 */
function ThreadHoverCard({ t: th, pos, today, anchorEl }) {
  const late = th.targetDeadline && th.targetDeadline < today
  const lateBy = late
    ? Math.round((new Date(today + 'T00:00:00') - new Date(th.targetDeadline + 'T00:00:00')) / 86400000)
    : 0
  const stalled = th.stillDays >= STALL_DAYS
  return (
    <FloatingCard anchorEl={anchorEl} style={S.hover}>
      <div style={S.hoverTop}>
        <span style={S.hoverNo}>{threadTag(th)}</span>
        <span style={{ ...S.hoverPos, color: stalled ? PALETTE.amber : 'var(--aj-text-mid)' }}>
          {hillRead(pos)} · {Math.round(pos * 100)}%
        </span>
      </div>
      <div style={S.hoverTitle}>{th.projectName || 'Project not named'}</div>
      <div style={S.hoverMs}>{th.title || 'Untitled thread'}</div>
      <div style={S.hoverRows}>
        {/* Crew (2026-09-01) — most threads never get one, so this row only appears once
            someone's actually been given the thread as a backup assignment (crewThread); unlike
            a milestone flight (always crewed, so HoverCard always shows the row), a bare
            "none assigned" on every single thread would just be noise. */}
        {th.crew && th.crew.length > 0 && (
          <Row k="Crew" v={th.crew.map(c => c.name + (c.isCaptain ? ' (capt)' : '')).join(', ')} />
        )}
        <Row k="Target" v={th.targetDeadline ? fmtDay(th.targetDeadline) + (late ? ` · ${lateBy}d late` : '') : 'undated'}
          tone={late ? PALETTE.red : null} />
        <Row k="Last activity" v={th.lastActivity ? fmtDay(th.lastActivity) : '—'} />
        <Row k="Last moved" v={th.stillDays === 0 ? 'today' : `${th.stillDays}d ago`}
          tone={stalled ? PALETTE.amber : null} />
      </div>
      {stalled && <div style={S.hoverWarn}>No one has moved this along the path in {th.stillDays} days.</div>}
    </FloatingCard>
  )
}

/**
 * `spin: true` gives the rotation its own transition, matched in duration/easing to the
 * position glide (the on-curve node's own `left`/`top` transition) so the nose swings continuously in
 * lockstep with the move rather than snapping to the new angle the instant a reload lands —
 * that mismatch (position gliding, angle jumping) is what read as "stepping."
 *
 * `spin: false` (dragging, the lifted ghost) is deliberately instant: attitudeFor(t) already
 * recomputes on every pointermove, so the angle is continuous by construction during a live
 * drag, and adding a transition there would only introduce lag against the cursor.
 */
// Basecamp-dots stacking (2026-08-27, Round 10, geometry fixed Round 11) — Matt: "Just stack
// the goddamn planes on the left, no expansion animation, just stack them. Like the dots stack
// in Basecamp... Stack the helicopters also on the left, right next to them." Round 10 shipped a
// diagonal step (both x and y), which read as a smeared blur, not a stack — Matt: "I want the
// stacked planes to be spread out and perfectly vertical, not overlapping diagonally like they
// are now. Same with helicopters." Round 11's fix: ONE fixed x per stack, items climb straight
// up with a gap wide enough that no two icons ever touch. No hover state, no open/closed
// distinction — every item here is always visible and always draggable onto the hill.
// Doubled 6→12 (2026-08-28) — Matt: "add capacity for twice as many planes and helicopters on
// the left and right side since there appears to be quite a bit more room for them." Still no
// scroll — past this it's a "+N" note, not another item.
const DECK_VISIBLE_CAP = 12
const STACK_GAP = 24                // px between stacked items, VERTICAL ONLY — wider than either
// icon's own rendered height so nothing overlaps, at any cap
const DECK_ICON = 18               // uniform icon size — no per-index scale-down, no per-index fade
const DECK_GAP = 18                // px between the flight stack and the thread stack next to it
const DECK_BOX_W = DECK_ICON + 4   // fixed horizontal footprint — just the icon; the stack itself
// never moves sideways
const DECK_BOX_H = (DECK_VISIBLE_CAP - 1) * STACK_GAP + DECK_ICON + 4

/**
 * The flight line — waiting milestones, stacked straight up at the gate, in priority order
 * (FlightTracker's own `lineItems` memo; no due-date window since 2026-09-23).
 * Anything past DECK_VISIBLE_CAP isn't shown here at all (no "+N" note any more — Matt: "there
 * is text above the planes and helicopters that overlap. Get rid of that text" — with two
 * stacks sitting this close together, both notes' own width collided; the same count is already
 * in the "Next up" list's own heading below, so nothing is actually lost by dropping it here).
 *
 * No dismiss × any more either (Matt: "get rid of the gray x next to planes") — hovering a
 * plane instead shows what it is (see the popover below), which is the thing worth improving
 * here, not a way to hide it.
 */
function FlightDeck({ items, today, onStartDrag, onOpen, onPutInAir, overlayOpen }) {
  const [hoverId, setHoverId] = useState(null)
  useEffect(() => { if (overlayOpen) setHoverId(null) }, [overlayOpen])   // see overlayOpen's prop comment
  const visible = items.slice(0, DECK_VISIBLE_CAP)
  return (
    <div style={{ position: 'relative', width: DECK_BOX_W, height: DECK_BOX_H }}>
      {/* Empty state is blank, not a message — Matt: with both the flight and thread stacks
          sitting this close together, their two empty-state chips overlapped into unreadable
          text the instant both were empty at once. No message reads better than a garbled one. */}
      {visible.map((m, i) => (
        <DeckFlightItem
          key={m.milestoneId} m={m} i={i} today={today}
          hovered={hoverId === m.milestoneId}
          onHoverIn={() => setHoverId(m.milestoneId)}
          onHoverOut={() => setHoverId(h => (h === m.milestoneId ? null : h))}
          onStartDrag={onStartDrag} onOpen={onOpen} onPutInAir={onPutInAir}
        />
      ))}
    </div>
  )
}

/**
 * One waiting milestone on the gate stack. A PRESS used to arm the drag immediately — clicking
 * without meaning to drag prompted a hill-move note for what was just a click (Matt, 2026-08-28:
 * "When I click a thread helicopter in the flight line... it immediately wants to put it on the
 * curve"). Now the same click-vs-drag boundary the on-curve icons use: move past DRAG_PROMOTE_PX
 * and it's the drag it always was; release in place and a menu opens instead. Carries the same
 * portal-bubble guard as OnCurveFlight — see that handler's comment for why it's load-bearing.
 */
function DeckFlightItem({ m, i, today, hovered, onHoverIn, onHoverOut, onStartDrag, onOpen, onPutInAir }) {
  const menu = useActionMenu()
  const pressRef = useRef(null)
  const rowRef = useRef(null)
  const late = m.latestFinish && m.latestFinish < today

  const handlePointerDown = (e) => {
    if (e.target && e.target.closest && e.target.closest('.wf-menu')) return
    e.preventDefault()
    pressRef.current = { x: e.clientX, y: e.clientY, moved: false }
    const onMove = (ev) => {
      const press = pressRef.current
      if (!press || press.moved) return
      if (Math.hypot(ev.clientX - press.x, ev.clientY - press.y) > DRAG_PROMOTE_PX) {
        press.moved = true
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        onStartDrag(m, ev)
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (pressRef.current && !pressRef.current.moved) {
        menu.toggle(rowRef.current)
        onHoverOut && onHoverOut()   // release the hover on click — see OnCurveFlight's comment
      }
      pressRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <div
      ref={rowRef}
      style={{
        position: 'absolute', left: 0, bottom: i * STACK_GAP, zIndex: hovered || menu.isOpen ? 60 : DECK_VISIBLE_CAP - i,
        cursor: 'grab', userSelect: 'none', touchAction: 'none',
      }}
      onPointerDown={handlePointerDown}
      onPointerEnter={onHoverIn}
      onPointerLeave={onHoverOut}
    >
      <Plane deg={0} color={late ? PALETTE.red : 'var(--aj-text-muted)'} size={DECK_ICON} spin={false} />
      {/* Anchored to the icon's own BOTTOM edge, growing upward only — never centered.
          These icons sit low, near the ground; a vertically-centered popover would hang
          half below them and get clipped by `wrap`'s own overflow:hidden (Matt: "cards
          are getting trapped inside the frame... don't show fully"). */}
      {(hovered || menu.isOpen) && (
        <div style={{ ...S.hover, position: 'absolute', left: DECK_ICON + 8, bottom: -6 }}>
          <div style={S.hoverTitle}>{m.projectName || 'Project not named'}</div>
          <div style={S.hoverMs}>{m.name}</div>
          <div style={S.hoverRows}>
            {m.phaseName && <Row k="Phase" v={m.phaseName} />}
            <Row k="Due" v={m.latestFinish ? fmtDay(m.latestFinish) : 'undated'} tone={late ? PALETTE.red : null} />
          </div>
        </div>
      )}
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onOpen && onOpen(m) }}>Open milestone</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); onPutInAir && onPutInAir(m) }}
          title="Crew it and put it on the curve at takeoff position">
          Put in the air…
        </ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/**
 * The thread line's own stack, right next to FlightDeck — same static, no-animation treatment,
 * for threads still sitting at their seed position (see `queuedThreads`' own comment in the main
 * component). Dragging one out is the same `startDrag('thread', …)` any on-curve thread already
 * uses — there is no separate "queue" data model, just a different starting point on screen.
 */
function ThreadDeck({ items, today, onStartDrag, onOpen, onPutInAir, onFastTrack, overlayOpen }) {
  const [hoverId, setHoverId] = useState(null)
  useEffect(() => { if (overlayOpen) setHoverId(null) }, [overlayOpen])   // see overlayOpen's prop comment
  const visible = items.slice(0, DECK_VISIBLE_CAP)
  return (
    <div style={{ position: 'relative', width: DECK_BOX_W, height: DECK_BOX_H }}>
      {/* Blank when empty — see the matching comment on FlightDeck above. */}
      {visible.map((th, i) => (
        <DeckThreadItem
          key={th.threadId} th={th} i={i} today={today}
          hovered={hoverId === th.threadId}
          onHoverIn={() => setHoverId(th.threadId)}
          onHoverOut={() => setHoverId(h => (h === th.threadId ? null : h))}
          onStartDrag={onStartDrag} onOpen={onOpen} onPutInAir={onPutInAir} onFastTrack={onFastTrack}
        />
      ))}
    </div>
  )
}

/** One queued thread on the gate stack — same click-vs-drag + menu treatment as DeckFlightItem
 * above (see its comment), with the thread controller's choice Matt asked for: "a popup menu
 * that lets us decide if we want to place into take off position... or fast-track it." */
function DeckThreadItem({ th, i, today, hovered, onHoverIn, onHoverOut, onStartDrag, onOpen, onPutInAir, onFastTrack }) {
  const menu = useActionMenu()
  const pressRef = useRef(null)
  const rowRef = useRef(null)
  const late = th.targetDeadline && th.targetDeadline < today
  const stalled = th.stillDays >= STALL_DAYS

  const handlePointerDown = (e) => {
    if (e.target && e.target.closest && e.target.closest('.wf-menu')) return
    e.preventDefault()
    pressRef.current = { x: e.clientX, y: e.clientY, moved: false }
    const onMove = (ev) => {
      const press = pressRef.current
      if (!press || press.moved) return
      if (Math.hypot(ev.clientX - press.x, ev.clientY - press.y) > DRAG_PROMOTE_PX) {
        press.moved = true
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        onStartDrag(th, ev)
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (pressRef.current && !pressRef.current.moved) {
        menu.toggle(rowRef.current)
        onHoverOut && onHoverOut()   // release the hover on click — see OnCurveFlight's comment
      }
      pressRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <div
      ref={rowRef}
      style={{
        position: 'absolute', left: 0, bottom: i * STACK_GAP, zIndex: hovered || menu.isOpen ? 60 : DECK_VISIBLE_CAP - i,
        cursor: 'grab', userSelect: 'none', touchAction: 'none',
      }}
      onPointerDown={handlePointerDown}
      onPointerEnter={onHoverIn}
      onPointerLeave={onHoverOut}
    >
      <Plane deg={0} icon="heli" color={stalled ? PALETTE.amber : late ? PALETTE.red : PALETTE.teal} size={DECK_ICON} spin={false} />
      {/* Same bottom-anchored, upward-only growth as FlightDeck's popover above — see
          its own comment. */}
      {(hovered || menu.isOpen) && (
        <div style={{ ...S.hover, position: 'absolute', left: DECK_ICON + 8, bottom: -6 }}>
          <div style={S.hoverTop}><span style={S.hoverNo}>{threadTag(th)}</span></div>
          <div style={S.hoverTitle}>{th.projectName || 'Project not named'}</div>
          <div style={S.hoverMs}>{th.title || 'Untitled thread'}</div>
          <div style={S.hoverRows}>
            <Row k="Target" v={th.targetDeadline ? fmtDay(th.targetDeadline) : 'undated'} tone={late ? PALETTE.red : null} />
            <Row k="Waiting" v={th.stillDays === 0 ? 'since today' : `${th.stillDays}d`} tone={stalled ? PALETTE.amber : null} />
          </div>
        </div>
      )}
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onOpen && onOpen(th) }}>Open thread</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); onPutInAir && onPutInAir(th) }}
          title="Give it a takeoff position on the curve — no explanation needed for this first move">
          Put in the air…
        </ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); onFastTrack && onFastTrack(th) }}
          title="Close administratively: complete open subtasks + record a Fast-tracked closure">
          Fast track to landing
        </ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/**
 * The right-side mirror of the gate stacks (Matt: "on the right side, we want stacks of planes
 * and helicopters that build up with the most recent completion on the bottom, getting pushed
 * upward... like a mirror of the left side in the grand sense"). True mirror arrangement: on
 * the left the planes hug the edge with the helicopters just inside them, so here the planes
 * hug the RIGHT edge with the helicopters inside. Items arrive newest-first from both APIs and
 * stack from the ground up, so the most recent landing sits at the bottom and pushes the rest
 * upward — exactly the left stacks' geometry. Read-only: nothing here is draggable; clicking
 * opens the milestone/thread, hovering shows the same style of card the left stacks show
 * (opening LEFT of the icon, since these sit at the right edge).
 */
function LandedStacks({ flights, threads, groundGap, landing, overlayOpen, onOpenFlight, onOpenThread }) {
  return (
    <div style={{ position: 'absolute', right: 16, bottom: groundGap, zIndex: 45, display: 'flex', alignItems: 'flex-end', gap: DECK_GAP }}>
      <LandedThreadDeck items={threads || []} onOpen={onOpenThread} overlayOpen={overlayOpen}
        landing={landing && landing.kind === 'thread' ? landing.id : null} />
      <LandedFlightDeck items={flights || []} onOpen={onOpenFlight} overlayOpen={overlayOpen}
        landing={landing && landing.kind === 'flight' ? landing.id : null} />
    </div>
  )
}

// The make-room choreography (Matt: "It should look like the stack is moving up to allow the
// newly finished plane or helicopter to slide in underneath"): while a landing ghost is inbound
// (`landing` = the id being landed), the stack (a) hides that item if the reload already
// delivered it — the ghost is playing its part — and (b) shifts everyone up ONE SLOT, animated
// by the CSS transition on `bottom`. When the ghost settles and the swap happens, the list has
// grown by one, so every older item's natural position IS the shifted position it's already at
// — nobody moves a pixel — and the real item renders exactly where the ghost sat.
const stackSlotStyle = (i, shifted, z) => ({
  position: 'absolute', right: 0, bottom: (i + (shifted ? 1 : 0)) * STACK_GAP, zIndex: z,
  cursor: 'pointer', userSelect: 'none',
  transition: 'bottom 420ms cubic-bezier(.22,.8,.3,1)',
})

function LandedFlightDeck({ items, onOpen, landing, overlayOpen }) {
  const [hoverId, setHoverId] = useState(null)
  useEffect(() => { if (overlayOpen) setHoverId(null) }, [overlayOpen])   // see overlayOpen's prop comment
  const shown = landing ? items.filter(f => f.flightId !== landing && f.milestoneId !== landing) : items
  // Capped at DECK_VISIBLE_CAP same as ever — only the "+N more posted" announcement is gone
  // (Matt, 2026-08-31, after the backfill-landing fix populated this stack past the cap for the
  // first time). Anything past the cap is still there, just not called out; each visible item
  // is still clickable.
  const visible = shown.slice(0, DECK_VISIBLE_CAP)
  return (
    <div style={{ position: 'relative', width: DECK_BOX_W, height: DECK_BOX_H }}>
      {/* Blank when empty — see the matching comment on FlightDeck above. */}
      {visible.map((f, i) => {
        const hovered = hoverId === f.flightId
        return (
          <div
            key={f.flightId}
            style={stackSlotStyle(i, !!landing, hovered ? 60 : DECK_VISIBLE_CAP - i)}
            onClick={() => { setHoverId(null); onOpen && onOpen(f) }}
            onPointerEnter={() => setHoverId(f.flightId)}
            onPointerLeave={() => setHoverId(h => (h === f.flightId ? null : h))}
          >
            <Plane deg={0} color={PALETTE.green} size={DECK_ICON} spin={false} />
            {/* Same bottom-anchored card the gate stacks show, opening LEFT (right edge). */}
            {hovered && (
              <div style={{ ...S.hover, position: 'absolute', right: DECK_ICON + 8, bottom: -6 }}>
                <div style={S.hoverTop}>
                  <span style={S.hoverNo}>{f.flightNumber || flightTag(f)}</span>
                  <span style={{ ...S.hoverPos, color: PALETTE.green }}>landed</span>
                </div>
                <div style={S.hoverTitle}>{f.projectName || 'Project not named'}</div>
                <div style={S.hoverMs}>{f.name}</div>
                <div style={S.hoverRows}>
                  {f.typeName && <Row k="Type" v={f.typeName} />}
                  <Row k="Landed" v={f.landedAt ? fmtDay(String(f.landedAt).slice(0, 10)) : '—'} />
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** The helicopters' own landed stack — recently CLOSED threads, same treatment as the planes. */
function LandedThreadDeck({ items, onOpen, landing, overlayOpen }) {
  const [hoverId, setHoverId] = useState(null)
  useEffect(() => { if (overlayOpen) setHoverId(null) }, [overlayOpen])   // see overlayOpen's prop comment
  const shown = landing ? items.filter(th => th.threadId !== landing) : items
  const visible = shown.slice(0, DECK_VISIBLE_CAP)
  return (
    <div style={{ position: 'relative', width: DECK_BOX_W, height: DECK_BOX_H }}>
      {/* Blank when empty — see the matching comment on FlightDeck above. */}
      {visible.map((th, i) => {
        const hovered = hoverId === th.rowId
        return (
          <div
            key={th.rowId || th.threadId}
            style={stackSlotStyle(i, !!landing, hovered ? 60 : DECK_VISIBLE_CAP - i)}
            onClick={() => { setHoverId(null); onOpen && onOpen(th) }}
            onPointerEnter={() => setHoverId(th.rowId)}
            onPointerLeave={() => setHoverId(h => (h === th.rowId ? null : h))}
          >
            <Plane deg={0} icon="heli" color={PALETTE.green} size={DECK_ICON} spin={false} />
            {hovered && (
              <div style={{ ...S.hover, position: 'absolute', right: DECK_ICON + 8, bottom: -6 }}>
                <div style={S.hoverTop}>
                  <span style={S.hoverNo}>{threadTag(th)}</span>
                  <span style={{ ...S.hoverPos, color: PALETTE.green }}>closed</span>
                </div>
                <div style={S.hoverTitle}>{th.projectName || 'Project not named'}</div>
                <div style={S.hoverMs}>{th.title || 'Untitled thread'}</div>
                <div style={S.hoverRows}>
                  <Row k="Closed" v={th.landedAt ? fmtDay(String(th.landedAt).slice(0, 10)) : '—'} />
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

const ICON_DEFS = {
  flight: { id: 'aj-ft-plane', ratio: 60 / 82 },
  heli: { id: 'aj-ft-heli', ratio: 135 / 110 },
}

/** `icon`: 'flight' (default, the milestone curve's stylized plane) or 'heli' (threads). */
export function Plane({ deg, color, size, spin, icon }) {
  const def = ICON_DEFS[icon] || ICON_DEFS.flight
  return (
    <svg width={size} height={Math.round(size * def.ratio)} style={{
      display: 'block',
      transform: `rotate(${deg}deg)`,
      transformOrigin: '50% 50%',
      transition: spin ? 'transform 420ms cubic-bezier(.22,.8,.3,1)' : undefined,
    }}>
      <use href={'#' + def.id} fill={color} />
    </svg>
  )
}

export const PlaneDefs = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
    <symbol id="aj-ft-plane" viewBox="8 23 82 60">
      <path fillRule="evenodd" d="m17.934 62.523-7.6484-8.418c-0.23438-0.25781-0.33203-0.57812-0.27734-0.92578 0.054688-0.34375 0.24219-0.62109 0.54688-0.79688l4.3398-2.5039c0.32031-0.1875 0.67969-0.20312 1.0195-0.050781l10.188 4.6094c0.33984 0.15625 0.70313 0.13672 1.0273-0.054688l15.418-9.0586c0.28906-0.16797 0.46094-0.44141 0.49219-0.77344s-0.09375-0.63281-0.34766-0.85156l-12.906-11.074c-0.28516-0.24219-0.42188-0.57812-0.38672-0.95312 0.035156-0.375 0.23047-0.67969 0.55469-0.86719l6.0195-3.4727c0.29297-0.16797 0.60938-0.19922 0.92969-0.085937l19.688 6.9141 2.6641 0.9375c0.28906 0.10156 0.57812 0.074219 0.83984-0.082031l15.754-9.2578c4.6016-2.6641 9.1523-2.5469 13.652 0.35156 0.51562 0.33203 0.66797 1.0156 0.33984 1.5352-2.3516 3.75-9.3945 8.8984-21.117 15.445-0.30078 0.16797-0.49219 0.43359-0.55469 0.76953l-5.2461 27.906c-0.0625 0.33203-0.24609 0.59375-0.53906 0.76172l-6.0195 3.4766c-0.32422 0.1875-0.6875 0.20312-1.0273 0.046875-0.33984-0.15625-0.5625-0.44141-0.63281-0.80859l-3.7227-19.82c-0.070313-0.36719-0.29297-0.65234-0.63281-0.80859s-0.70312-0.14062-1.0273 0.046875l-16.617 9.5938c-0.023437 0.015625-0.046875 0.027344-0.070313 0.039063-6.5859 3.1914-11.465 2.6328-14.625-1.6758-0.027343-0.035157-0.046874-0.0625-0.074218-0.09375z" />
    </symbol>
    {/* Threads' icon (2026-08-27, replacing an earlier hand-built Cessna): Matt supplied a
        helicopter silhouette (Noun Project, "Helicopter" by Lewen Design — attribution text
        stripped, only the glyph path kept) and asked for it in place of the fixed-wing plane.
        Used exactly as supplied, unmirrored — unlike the Cessna it replaced, Matt did not say
        this one needed flipping, and reading the path by hand (cockpit/chin bubble vs. tail
        rotor) was not confident enough to justify a mirror transform with no live render to
        check it against. If it reads backwards on screen, flip it with a wrapping
        `<g transform="scale(-1,1) translate(-100,0)">`. */}
    <symbol id="aj-ft-heli" viewBox="-5 -10 110 135">
      <path fillRule="evenodd" d="m69.914 35.141h-0.43359v-0.19531c0-2.2812-1.8516-4.1328-4.1328-4.1328h-2.8398v-3.3477h30.758c0.95703 0 1.7305-0.77344 1.7305-1.7305s-0.77344-1.7305-1.7305-1.7305l-65.148-0.003906c-0.95703 0-1.7305 0.77344-1.7305 1.7305s0.77344 1.7305 1.7305 1.7305h30.758v3.3477h-3.3359c-2.2812 0-4.1328 1.8516-4.1328 4.1328v0.19531h-2.7266c-4.1562 0-7.6836 2.4258-9.3633 5.8711h-21.148l-6.2305-7.9922c-0.29688-0.34375-0.72656-0.54297-1.1797-0.54297h-4.1953c-1.0781 0-1.832 1.0703-1.4609 2.0859l3.9102 10.766-3.9102 10.766c-0.36719 1.0156 0.38281 2.0859 1.4609 2.0859h4.1953c0.45313 0 0.88672-0.19922 1.1797-0.54297l4.6055-5.9062 23.125 3.8789c1.7539 6.0117 6.6445 10.586 12.703 11.984l-1.9961 4.9883h-7.125c-0.94141 0-1.707 0.76562-1.707 1.707s0.76562 1.707 1.707 1.707h39.125c1.8984 0 3.7188-0.90234 4.8672-2.4102l1.5117-1.9844c0.57031-0.75 0.42578-1.8203-0.32422-2.3945-0.75-0.57031-1.8203-0.42578-2.3945 0.32422l-1.5117 1.9844c-0.50781 0.66797-1.3125 1.0625-2.1484 1.0625h-5.625l-1.8125-4.5312h5.418c7.9609 0 14.445-5.1055 14.293-12.441-0.054688-2.6328-0.65625-5.5508-2.7773-8.625-4.4141-6.3906-11.301-11.848-21.953-11.848zm-15.859 37.441 1.8203-4.5469c0.13281 0.003906 0.25781 0.019532 0.39062 0.019532h14.988l1.8125 4.5312h-19.016zm35.152-17.723c-0.35938 0.47266-0.92188 0.75-1.5156 0.75h-16.926c-1.0508 0-1.9023-0.85156-1.9023-1.9023v-11.672c0-1.0508 0.85156-1.9023 1.9023-1.9023h0.89453c8.3828 0 15.73 5.375 17.867 13.066 0.16016 0.57422 0.042968 1.1875-0.32031 1.6602z" />
    </symbol>
  </svg>
)

const S = {
  wrap: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 10, overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'baseline', gap: 10, padding: '12px 16px 10px', borderBottom: '0.5px solid var(--aj-border)' },
  title: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.09em' },
  sub: { fontSize: 11, color: 'var(--aj-text-muted)' },
  select: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '4px 8px', width: 220, boxSizing: 'border-box' },
  // ScopePicker's own dropdown — same floating-menu language as the rest of the app (bordered
  // card, own shadow), positioned directly under the input.
  scopeMenu: {
    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, zIndex: 60,
    background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6,
    boxShadow: '0 6px 20px rgba(7,39,45,0.18)', maxHeight: 260, overflowY: 'auto', padding: '4px 0',
  },
  scopeItem: {
    display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', border: 'none',
    background: 'none', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5,
    color: 'var(--aj-text)',
  },
  scopeEmpty: { padding: '6px 10px', fontSize: 11, color: 'var(--aj-text-muted)', fontStyle: 'italic' },
  scheduleBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 500, color: 'var(--aj-accent-blue)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '4px 9px', marginRight: 8, cursor: 'pointer', whiteSpace: 'nowrap' },

  // Fixed height. A flex row with only a minHeight once stretched to fit the full flight line —
  // 28 items — which made this (100% of the row) hundreds of pixels tall and turned the hill
  // into a needle. Fixed height plus the deck's own internal scroll (see FlightDeck) is what
  // keeps the curve's aspect stable no matter how much is waiting.
  chart: { position: 'relative', minWidth: 320, minHeight: 0 },
  chartEmpty: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic', pointerEvents: 'none' },
  // Header-row readout now, not chart-corner — see the header's own comment for why.
  crewStat: {
    display: 'flex', alignItems: 'baseline', gap: 5, marginRight: 10, whiteSpace: 'nowrap',
    fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)',
  },
  crewStatSep: { color: 'var(--aj-border-mid)' },
  crewStatN: { fontFamily: '"DM Mono", monospace', fontWeight: 600, color: 'var(--aj-text)' },

  // The board tab strip (2026-09-10) — switches the chart's own footprint between the flight
  // curve and the pilots table. Replaced a hover-only popover on the crew-count stat, which
  // Matt called "too hidden."
  boardTabs: {
    display: 'flex', gap: 2, padding: '8px 16px 0', borderBottom: '0.5px solid var(--aj-border)',
  },
  boardTab: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 500, color: 'var(--aj-text-muted)',
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    padding: '0 4px 8px', marginRight: 14, cursor: 'pointer',
  },
  boardTabActive: { color: 'var(--aj-text)', fontWeight: 600, borderBottomColor: 'var(--aj-accent-blue)' },

  ticketBase: { flex: 'none', borderRadius: '8px 0 0 8px', overflow: 'hidden' },
  ticketGrid: { display: 'grid', transition: TICKET_TRANSITION },
  ticketHeadCell: {
    padding: '9px 8px 9px 10px', fontFamily: '"Montserrat", sans-serif', fontSize: 8.5, fontWeight: 600,
    letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden',
  },
  ticketNameCell: { display: 'flex', alignItems: 'center', padding: '9px 8px 9px 10px', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  ticketSlotCol: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  ticketSlot: { display: 'flex', alignItems: 'center', gap: 4, padding: '5px 8px 5px 10px', minHeight: 24, boxSizing: 'border-box', whiteSpace: 'nowrap' },
  // Pilots tab (2026-09-10) — the clickable 1st/2nd rank pill on a flying seat, and the "+ Add
  // to flight" picker on a grounded one. Small enough to sit inline in the ticket's own row.
  pilotRankBtn: {
    flexShrink: 0, fontFamily: '"DM Mono", monospace', fontSize: 9.5, fontWeight: 700,
    color: 'var(--aj-accent-blue)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 10, padding: '1px 6px', cursor: 'pointer',
  },
  pilotAddSelect: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-accent-blue)',
    background: 'none', border: 'none', cursor: 'pointer', width: '100%', padding: 0,
  },
  // The torn-ticket fold edge — the whole strip is the click target (per the design spec: "the
  // fold edge is the button. Nothing else in the stub is clickable"). Round 1 paired
  // `--aj-bg-subtle`/`--aj-bg-elevated` (a ~1% lightness difference, invisible). Round 2 tried
  // `--aj-bg-muted`/`--aj-bg-page` (a real but still-pastel difference) — Matt, live, twice:
  // "nothing to indicate a fold" / "very very light in color." A subtle color illusion is the
  // wrong primary affordance here; `--aj-border-mid` as a SOLID fill (not a hairline) gives a
  // strip that's unmistakably tinted (not pastel-adjacent-to-white) regardless of monitor/theme,
  // and the chevron icon below removes any dependence on color contrast entirely.
  ticketEdge: {
    flex: 'none', width: 16, display: 'flex', flexDirection: 'column', cursor: 'pointer',
    background: 'var(--aj-border-mid)', borderRadius: '0 8px 8px 0', borderLeft: '0.5px solid var(--aj-border)',
  },
  ticketTooth: {
    flex: '1 1 auto', width: 0, height: 0,
    borderTop: '7px solid transparent', borderBottom: '7px solid transparent',
    borderLeft: '16px solid var(--aj-bg-page)',
  },
  ticketEdgeChevron: {
    position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)',
    display: 'flex', justifyContent: 'center', pointerEvents: 'none',
  },
  // `top` is supplied per instance (relative to the MAIN curve's own baseY, not the container's
  // bottom edge) so these stay anchored to the milestone curve regardless of how much room the
  // threads band below adds to the container's total height.
  zone: { position: 'absolute', textAlign: 'center', fontFamily: '"Montserrat", sans-serif', fontSize: 8.5, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--aj-text-muted)', opacity: 0.75, pointerEvents: 'none' },
  endMark: {
    fontFamily: '"DM Mono", monospace', fontSize: 10.5, fontWeight: 500, whiteSpace: 'nowrap',
    color: 'var(--aj-text-mid)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 20, padding: '3px 9px', boxShadow: '0 2px 6px rgba(7,39,45,0.16)', pointerEvents: 'none', zIndex: 25,
  },

  // FlightDeck/ThreadDeck/LandedDeck — the static Basecamp-dots stacks. Only per-item content
  // styling lives here; each stack's own container (size, position) stays inline where
  // DECK_BOX_W/DECK_BOX_H and friends live together. (No empty-state chip — see the comment
  // at FlightDeck's own empty check. No "+N more" overflow note either, on any of the three —
  // LandedFlightDeck's own was the last one and was dropped 2026-08-31.)

  // Centered with margins, NOT a transform: the ft-stall keyframes animate `transform: scale()`,
  // which overrode a translate(-50%,-50%) and left the disc hanging off the plane's lower right.
  stallRing: { position: 'absolute', left: 0, top: 0, marginLeft: -17, marginTop: -17, width: 34, height: 34, borderRadius: '50%', background: PALETTE.amber, animation: 'ft-stall 2.4s ease-in-out infinite', pointerEvents: 'none' },
  // Something in the way, per the captain's own reports (2026-09-16). Same disc, red.
  blockRing: { position: 'absolute', left: 0, top: 0, marginLeft: -17, marginTop: -17, width: 34, height: 34, borderRadius: '50%', background: PALETTE.red, animation: 'ft-stall 2.4s ease-in-out infinite', pointerEvents: 'none' },
  // Shown ONLY while actively dragging a flight/thread past LANDING_T — tells you what letting
  // go right now will do, before you do it. Matt: "I want a message to show that it will make it
  // landed if I let go." Mirrors endMark's pill language (own border/shadow/radius) but in green,
  // since it is announcing the same thing LandedDeck's own green planes represent.
  landPreview: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap',
    color: '#fff', background: PALETTE.green, borderRadius: 20, padding: '4px 10px',
    boxShadow: '0 3px 10px rgba(7,39,45,0.24)', pointerEvents: 'none', zIndex: 45,
  },
  tag: { fontFamily: '"DM Mono", monospace', fontSize: 9.5, color: 'var(--aj-text-muted)', whiteSpace: 'nowrap', opacity: 0.7, transition: 'opacity 140ms, color 140ms' },
  tagOn: { opacity: 1, color: 'var(--aj-text)' },
  movedRing: { position: 'absolute', left: 0, top: 0, marginLeft: -17, marginTop: -17, width: 34, height: 34, borderRadius: '50%', border: '1px solid ' + PALETTE.blue, opacity: 0.4, pointerEvents: 'none' },

  // position/zIndex/top/left all come from FloatingCard itself now (see its own doc comment) —
  // this only carries the card's visual chrome.
  hover: { width: HOVER_W, padding: '10px 12px', borderRadius: 8, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', boxShadow: '0 8px 22px rgba(7,39,45,0.20)', pointerEvents: 'none', textAlign: 'left', animation: 'ft-in 140ms ease both' },
  hoverTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 5 },
  hoverNo: { fontFamily: '"DM Mono", monospace', fontSize: 12, fontWeight: 500, color: 'var(--aj-text)' },
  hoverPos: { fontSize: 10, whiteSpace: 'nowrap' },
  hoverTitle: { fontSize: 12, color: 'var(--aj-text)', lineHeight: 1.3 },
  hoverMs: { fontSize: 11, color: 'var(--aj-text-muted)', lineHeight: 1.35, marginTop: 1, marginBottom: 7 },
  hoverRows: { display: 'flex', flexDirection: 'column', gap: 3, borderTop: '0.5px solid var(--aj-border)', paddingTop: 6 },
  hoverRow: { display: 'flex', gap: 8, fontSize: 10.5, lineHeight: 1.4 },
  hoverK: { flex: '0 0 66px', color: 'var(--aj-text-muted)' },
  hoverV: { flex: '1 1 auto', minWidth: 0 },
  hoverWarn: { marginTop: 7, paddingTop: 6, borderTop: '0.5px solid var(--aj-border)', fontSize: 10.5, color: PALETTE.amber, lineHeight: 1.4 },
  // The action row on the hover card (2026-09-10) — the click menu's five items as small buttons.
  hoverActions: { display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8, paddingTop: 7, borderTop: '0.5px solid var(--aj-border)' },
  hoverBtn: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, lineHeight: 1.2, padding: '4px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer', whiteSpace: 'nowrap' },
  hoverBtnDanger: { color: PALETTE.red, borderColor: 'rgba(220,38,38,0.35)' },

  scrub: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px', borderTop: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-subtle)' },
  scrubBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 15, lineHeight: 1, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '3px 10px', cursor: 'pointer' },
  scrubMid: { display: 'flex', flexDirection: 'column', minWidth: 150 },
  scrubDay: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text)' },
  scrubMeta: { fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 1 },
  ticks: { flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: 3, height: 18, overflowX: 'auto' },
  tick: { width: 3, borderRadius: 2, border: 'none', padding: 0, cursor: 'pointer', flex: '0 0 auto', transition: 'height 140ms, opacity 140ms' },
  liveBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#fff', background: '#07272D', border: '0.5px solid #07272D', borderRadius: 4, padding: '4px 11px', cursor: 'pointer', whiteSpace: 'nowrap' },

  notes: { padding: '10px 16px 12px', borderTop: '0.5px solid var(--aj-border)', display: 'flex', flexDirection: 'column', gap: 6 },
  noteEmpty: { fontSize: 11.5, color: 'var(--aj-text-muted)', fontStyle: 'italic' },
  note: { display: 'flex', gap: 9, fontSize: 11.5, lineHeight: 1.45 },
  noteNo: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', flex: '0 0 62px' },
  noteTxt: { color: 'var(--aj-text)' },
}
